import { sortBy, uniq } from 'lodash';
import { ServiceTypeCode } from 'x12-standard/element/1365';
import { PlaceOfServiceCode } from '../benefit';
import { Cpt } from '../cpt';
import { StcException, TreatmentType } from '../estimation';
import { LocationRateType } from '../location';
import { Option } from '../options';

export function serviceTypesFromCpt(
  cpts: string[],
  posInput: PlaceOfServiceCode | null,
  facilityType: LocationRateType | null,
): ServiceTypeCode[] {
  const allPossiblyRelevantSts: ServiceTypeCode[] = allServiceTypesFromCpt(
    cpts,
    posInput,
    facilityType,
  );
  return allPossiblyRelevantSts?.length > 0 ? [allPossiblyRelevantSts[0]] : [];
}

export function serviceTypesFromExceptions(
  payerIds: (number | null)[],
  placeOfServiceCode: PlaceOfServiceCode | null,
  treatmentType: TreatmentType,
  cpts: string[],
  insuranceProductId: number | null,
  stcExceptions: StcException[],
): StcException[] {
  const primaryPayerId = payerIds[0];

  if (!primaryPayerId) {
    return [];
  }

  return stcExceptions
    .filter(stce =>
      stcExceptionMatches(
        stce,
        primaryPayerId,
        placeOfServiceCode,
        treatmentType,
        cpts,
        insuranceProductId,
      ),
    )
    .sort(
      (stce1, stce2) =>
        ST_PRIORITIZATION.indexOf(<ServiceTypeCode>stce1.serviceType) -
        ST_PRIORITIZATION.indexOf(<ServiceTypeCode>stce2.serviceType),
    );
}

export function stcExceptionMatches(
  stcException: StcException,
  payerId: number,
  placeOfServiceCode: PlaceOfServiceCode | null,
  treatmentType: TreatmentType,
  cpts: string[],
  insuranceProductId: number | null,
): boolean {
  return (
    stcException.payerIds.includes(String(payerId)) &&
    !!placeOfServiceCode &&
    stcException.placeOfServiceCodes.includes(placeOfServiceCode) &&
    (stcException.treatmentTypes.includes(treatmentType) ||
      stcException.treatmentTypes.length === 0) &&
    cpts.some(cpt => stcException.codes.includes(cpt)) &&
    (stcException.insuranceProductIds.length === 0 ||
      (insuranceProductId !== null &&
        stcException.insuranceProductIds.includes(String(insuranceProductId))))
  );
}

// This function exists so we can preserve our test cases about getting all the STC's from CPT's.
// If we are certain we will only ever want the primary service type, this function doesn't serve a purpose.
export function allServiceTypesFromCpt(
  cpts: string[],
  posInput: PlaceOfServiceCode | null,
  facilityType: LocationRateType | null,
): ServiceTypeCode[] {
  const catISt: ServiceTypeCode[] = [];
  const catIISt: ServiceTypeCode[] = [];

  cpts.forEach(cpt => {
    let savedCatISt = false;
    // it is possible to hit the most general mapping first
    // and then find a more specific one in subsequent rows
    // based on the csv format - so look through each row
    // of the mapping
    for (const catICpt of CATEGORY_I_MAPPING) {
      const { start, end, pos: posRaw } = catICpt;
      let st = <ServiceTypeCode>catICpt.service_type;
      st = adjustServiceType(posInput, facilityType, st);

      // only looking for unique codes of cominbed Category I and II
      if (catISt.includes(st)) {
        continue;
      }

      const posSpec = convertToPlaceOfServce(posRaw);
      const posMatch = posSpec === null || posSpec === posInput;

      if (isMatch(start, end, cpt) && posMatch) {
        // if I do find a more specific st mapping replace the more
        // general st mapping
        if (savedCatISt) {
          catISt[catISt.length - 1] = st;
        } else {
          catISt.push(st);
          savedCatISt = true;
        }
        // if place of service match that is the most specific
        // mapping - so stop looking
        if (posInput && posSpec && posInput === posSpec) {
          break;
        }
      }
    }

    let savedCatIISt = false;
    for (const catIICpt of CATEGORY_II_MAPPING) {
      const { start, end } = catIICpt;
      let st = <ServiceTypeCode>catIICpt.service_type;
      st = adjustServiceType(posInput, facilityType, st);

      if (catIISt.includes(st) || catISt.includes(st)) {
        continue;
      }

      if (isMatch(start, end, cpt)) {
        if (savedCatIISt) {
          catIISt[catIISt.length - 1] = st;
        } else {
          catIISt.push(st);
          savedCatIISt = true;
        }
      }
    }
  });

  const sortedCatISt = sortBy(catISt, st => ST_PRIORITIZATION.indexOf(st));
  const sortedCatIISt = sortBy(catIISt, st => ST_PRIORITIZATION.indexOf(st));

  const cptSt = uniq([...sortedCatISt, ...sortedCatIISt]);

  const allPossiblyRelevantSts = uniq(
    cptSt.reduce((all, st) => [...all, st], <ServiceTypeCode[]>[]),
  );

  return allPossiblyRelevantSts;
}

function adjustServiceType(
  pos: PlaceOfServiceCode | null,
  facilityType: LocationRateType | null,
  serviceType: ServiceTypeCode,
): ServiceTypeCode {
  if (
    pos == PlaceOfServiceCode.AMBULATORY_SURGICAL_CENTER &&
    facilityType === 'asc' &&
    serviceType == ServiceTypeCode.SURGICAL
  ) {
    return ServiceTypeCode.AMBULATORY_SERVICE_CENTER_FACILITY;
  }

  return serviceType;
}

function convertToPlaceOfServce(pos: string): PlaceOfServiceCode | null {
  return pos === ''
    ? null
    : ((+pos).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      }) as PlaceOfServiceCode);
}

function isMatch(start: string, end: string, cpt: string): boolean {
  const { letter: startLetter, code: startCode } = getCptSplit(start);
  const { code: endCode } = getCptSplit(end);
  const { letter: cptLetter, code: cptCode } = getCptSplit(cpt);

  return cptLetter === startLetter && inCptRange(startCode, endCode, cptCode);
}

function inCptRange(
  start: number | null,
  end: number | null,
  cpt: number | null,
) {
  // end may be null so check if cpt is equal to start first then
  // check in in range
  return (
    start != null &&
    cpt != null &&
    (cpt === start || (end != null && cpt > start && cpt <= end))
  );
}

function getCptSplit(cpt: string): { letter: string; code: number | null } {
  // codes generally have the format ##### or A####
  // extract the first letter if it exists and transform the code
  // into a number for numeric comparisons
  const code = cpt.replace(/^\D+/g, '');
  return {
    letter: cpt.replace(/[^A-Za-z]/g, ''),
    code: code === '' ? null : +code,
  };
}

export function cptToOption(cpt: Cpt): Option {
  return {
    id: cpt.code,
    title: cpt.accountDescription || cpt.description || '',
    subtitle: cpt.code,
  };
}

// the format of this data must the most general code mapping to more specific
// for example row 1: 00000 - 09999
//             row 2: 05000 - 06000
//             row 3: 05101
const CATEGORY_I_MAPPING: {
  start: string;
  end: string;
  pos: string;
  service_type: string;
}[] = [
  { start: '00000', end: '09999', pos: '', service_type: '7' },
  { start: '10021', end: '69990', pos: '', service_type: '2' },
  { start: '59400', end: '59430', pos: '', service_type: '69' },
  { start: '59510', end: '59515', pos: '', service_type: '69' },
  { start: '59610', end: '59622', pos: '', service_type: '69' },
  { start: '70010', end: '77086', pos: '', service_type: '4' },
  { start: '70336', end: '', pos: '', service_type: '62' },
  { start: '70450', end: '', pos: '', service_type: '62' },
  { start: '70460', end: '', pos: '', service_type: '62' },
  { start: '70470', end: '', pos: '', service_type: '62' },
  { start: '70480', end: '70488', pos: '', service_type: '62' },
  { start: '70490', end: '70498', pos: '', service_type: '62' },
  { start: '70540', end: '70553', pos: '', service_type: '62' },
  { start: '71250', end: '', pos: '', service_type: '62' },
  { start: '71260', end: '', pos: '', service_type: '62' },
  { start: '71270', end: '', pos: '', service_type: '62' },
  { start: '71275', end: '', pos: '', service_type: '62' },
  { start: '72125', end: '72133', pos: '', service_type: '62' },
  { start: '72141', end: '72158', pos: '', service_type: '62' },
  { start: '72191', end: '72194', pos: '', service_type: '62' },
  { start: '72195', end: '72197', pos: '', service_type: '62' },
  { start: '73200', end: '73206', pos: '', service_type: '62' },
  { start: '73218', end: '73223', pos: '', service_type: '62' },
  { start: '73700', end: '73706', pos: '', service_type: '62' },
  { start: '73718', end: '73723', pos: '', service_type: '62' },
  { start: '74150', end: '', pos: '', service_type: '62' },
  { start: '74160', end: '', pos: '', service_type: '62' },
  { start: '74170', end: '', pos: '', service_type: '62' },
  { start: '74174', end: '74178', pos: '', service_type: '62' },
  { start: '74181', end: '74185', pos: '', service_type: '62' },
  { start: '74261', end: '74263', pos: '', service_type: '62' },
  { start: '75571', end: '75574', pos: '', service_type: '62' },
  { start: '75635', end: '', pos: '', service_type: '62' },
  { start: '76376', end: '76377', pos: '', service_type: '73' },
  { start: '76380', end: '', pos: '', service_type: '62' },
  { start: '76497', end: '', pos: '', service_type: '62' },
  { start: '76506', end: '', pos: '', service_type: '73' },
  { start: '76510', end: '76519', pos: '', service_type: 'BR' },
  { start: '76529', end: '', pos: '', service_type: 'BR' },
  { start: '76536', end: '', pos: '', service_type: '73' },
  { start: '76604', end: '', pos: '', service_type: '73' },
  { start: '76641', end: '76642', pos: '', service_type: '73' },
  { start: '76700', end: '76706', pos: '', service_type: '73' },
  { start: '76770', end: '76776', pos: '', service_type: '73' },
  { start: '76800', end: '', pos: '', service_type: '73' },
  { start: '76801', end: '76817', pos: '', service_type: 'BU' },
  { start: '76818', end: '76819', pos: '', service_type: 'BU' },
  { start: '76820', end: '76821', pos: '', service_type: 'BU' },
  { start: '76825', end: '76828', pos: '', service_type: 'BU' },
  { start: '76830', end: '76857', pos: '', service_type: 'BV' },
  { start: '76870', end: '76873', pos: '', service_type: '73' },
  { start: '76881', end: '76882', pos: '', service_type: '73' },
  { start: '76885', end: '76886', pos: '', service_type: '73' },
  { start: '76930', end: '76965', pos: '', service_type: '2' },
  { start: '76975', end: '', pos: '', service_type: 'BN' },
  { start: '76977', end: '76983', pos: '', service_type: '73' },
  { start: '76998', end: '', pos: '', service_type: '2' },
  { start: '76999', end: '', pos: '', service_type: '1' },
  { start: '77014', end: '', pos: '', service_type: '62' },
  { start: '77021', end: '', pos: '', service_type: '62' },
  { start: '77059', end: '', pos: '', service_type: '62' },
  { start: '77261', end: '77799', pos: '', service_type: '6' },
  { start: '78012', end: '78999', pos: '', service_type: '73' },
  { start: '79005', end: '79999', pos: '', service_type: '6' },
  { start: '80047', end: '88299', pos: '', service_type: '5' },
  { start: '88300', end: '88399', pos: '', service_type: '2' },
  { start: '89250', end: '89398', pos: '', service_type: '82' },
  { start: '90281', end: '99607', pos: '20', service_type: 'UC' },
  { start: '90281', end: '90399', pos: '', service_type: '80' },
  { start: '90460', end: '90756', pos: '', service_type: '80' },
  { start: '90669', end: '', pos: '', service_type: '19' },
  { start: '90732', end: '', pos: '', service_type: '19' },
  { start: '91300', end: '91304', pos: '', service_type: '80' },
  { start: '90785', end: '90899', pos: '', service_type: 'A4' },
  { start: '90785', end: '90899', pos: '51', service_type: 'A7' },
  { start: '90785', end: '90899', pos: '52', service_type: 'BB' },
  { start: '90785', end: '90899', pos: '53', service_type: 'A8' },
  { start: '90785', end: '90899', pos: '2', service_type: 'A4' },
  { start: '90901', end: '90913', pos: '', service_type: 'PT' },
  { start: '90935', end: '90999', pos: '', service_type: '76' },
  { start: '91010', end: '91299', pos: '', service_type: 'BN' },
  { start: '96400', end: '', pos: '', service_type: '78' },
  { start: '96408', end: '96425', pos: '', service_type: '78' },
  { start: '96520', end: '', pos: '', service_type: '78' },
  { start: '96530', end: '', pos: '', service_type: '78' },
  { start: '92002', end: '92499', pos: '', service_type: 'AL' },
  { start: '92025', end: '', pos: '', service_type: '73' },
  { start: '92134', end: '', pos: '', service_type: '73' },
  { start: '92136', end: '', pos: '', service_type: '73' },
  { start: '92502', end: '92700', pos: '', service_type: '1' },
  { start: '92920', end: '93302', pos: '', service_type: '1' },
  { start: '92920', end: '93302', pos: '11', service_type: '98' },
  { start: '93303', end: '93356', pos: '', service_type: 'BL' },
  { start: '93571', end: '93572', pos: '', service_type: 'BL' },
  { start: '93668', end: '', pos: '', service_type: 'BG' },
  { start: '93880', end: '93882', pos: '', service_type: '73' },
  { start: '93886', end: '93893', pos: '', service_type: '73' },
  { start: '93925', end: '93990', pos: '', service_type: '73' },
  { start: '94002', end: '94005', pos: '11', service_type: 'PU' },
  { start: '94010', end: '94016', pos: '', service_type: 'PU' },
  { start: '94621', end: '94799', pos: '', service_type: '1' },
  { start: '95004', end: '95079', pos: '', service_type: '79' },
  { start: '95115', end: '95119', pos: '', service_type: 'GY' },
  { start: '95249', end: '95251', pos: '', service_type: 'BP' },
  { start: '95700', end: '95967', pos: '', service_type: '73' },
  { start: '95940', end: '95941', pos: '', service_type: '2' },
  { start: '95970', end: '96020', pos: '', service_type: '1' },
  { start: '96040', end: '', pos: '', service_type: '73' },
  { start: '96105', end: '96146', pos: '', service_type: '73' },
  { start: '96127', end: '', pos: '11', service_type: 'A8' },
  { start: '96127', end: '', pos: '02', service_type: 'A8' },
  { start: '96127', end: '', pos: '51', service_type: 'A7' },
  { start: '96127', end: '', pos: '52', service_type: 'BB' },
  { start: '96127', end: '', pos: '53', service_type: 'A8' },
  { start: '96127', end: '', pos: '56', service_type: 'A7' },
  { start: '96360', end: '96361', pos: '', service_type: '1' },
  { start: '96360', end: '96361', pos: '23', service_type: '86' },
  { start: '96360', end: '96361', pos: '21', service_type: '48' },
  { start: '96365', end: '96379', pos: '', service_type: '1' },
  { start: '96407', end: '96417', pos: '', service_type: '78' },
  { start: '96420', end: '96425', pos: '', service_type: '78' },
  { start: '96440', end: '96549', pos: '', service_type: '1' },
  { start: '96567', end: '96574', pos: '', service_type: 'DG' },
  { start: '96900', end: '96999', pos: '', service_type: 'DG' },
  { start: '97010', end: '97039', pos: '', service_type: 'AE' },
  { start: '97110', end: '97546', pos: '', service_type: 'AE' },
  { start: '97161', end: '97164', pos: '', service_type: 'AE' },
  { start: '97165', end: '97168', pos: '', service_type: 'AD' },
  { start: '97169', end: '97172', pos: '', service_type: 'AE' },
  { start: '97597', end: '97610', pos: '', service_type: 'AE' },
  { start: '97750', end: '97755', pos: '', service_type: 'AE' },
  { start: '97760', end: '97763', pos: '', service_type: 'AE' },
  { start: '97799', end: '', pos: '', service_type: 'AE' },
  { start: '97151', end: '97158', pos: '', service_type: 'MH' },
  { start: '97802', end: '97804', pos: '', service_type: '1' },
  { start: '97810', end: '97814', pos: '', service_type: '64' },
  { start: '98925', end: '98929', pos: '', service_type: '1' },
  { start: '98940', end: '98943', pos: '', service_type: '33' },
  { start: '98960', end: '98962', pos: '', service_type: 'CQ' },
  { start: '98966', end: '98972', pos: '', service_type: 'CQ' },
  { start: '99000', end: '99090', pos: '', service_type: 'CQ' },
  { start: '99091', end: '99474', pos: '', service_type: 'CQ' },
  { start: '99151', end: '99157', pos: '', service_type: '7' },
  { start: '99170', end: '99199', pos: '', service_type: '9' },
  { start: '99500', end: '99602', pos: '', service_type: '42' },
  { start: '99605', end: '99607', pos: '', service_type: 'CQ' },
  { start: '99202', end: '99205', pos: '', service_type: '98' },
  { start: '99202', end: '99205', pos: '24', service_type: '3' },
  { start: '99202', end: '99205', pos: '02', service_type: '98' },
  { start: '99202', end: '99215', pos: '19', service_type: '3' },
  { start: '99202', end: '99215', pos: '22', service_type: '3' },
  { start: '99212', end: '99215', pos: '', service_type: '98' },
  { start: '99221', end: '99223', pos: '21', service_type: '3' },
  { start: '99231', end: '99233', pos: '21', service_type: '3' },
  { start: '99234', end: '99236', pos: '21', service_type: '48' },
  { start: '99241', end: '99245', pos: '19', service_type: '3' },
  { start: '99241', end: '99245', pos: '22', service_type: '3' },
  { start: '99251', end: '99255', pos: '21', service_type: '3' },
  { start: '99281', end: '99285', pos: '', service_type: '3' },
  { start: '99281', end: '99285', pos: '23', service_type: '86' },
  { start: '99291', end: '99292', pos: '21', service_type: '48' },
  { start: '99304', end: '99318', pos: '31', service_type: '54' },
  { start: '99304', end: '99318', pos: '32', service_type: '54' },
  { start: '99324', end: '99337', pos: '', service_type: '54' },
  { start: '99339', end: '99340', pos: '', service_type: '54' },
  { start: '99341', end: '99350', pos: '', service_type: '42' },
  { start: '99354', end: '99360', pos: '', service_type: '98' },
  { start: '99363', end: '99368', pos: '', service_type: 'CQ' },
  { start: '99374', end: '99380', pos: '', service_type: 'CQ' },
  { start: '99381', end: '99397', pos: '11', service_type: 'BZ' },
  { start: '99421', end: '99429', pos: '', service_type: 'BZ' },
  { start: '99439', end: '99491', pos: '', service_type: 'CQ' },
  { start: '99450', end: '99458', pos: '', service_type: '1' },
  { start: '99460', end: '99463', pos: '', service_type: '65' },
  { start: '99464', end: '99465', pos: '', service_type: '69' },
  { start: '99466', end: '99486', pos: '', service_type: '48' },
  { start: '99483', end: '99499', pos: '', service_type: 'CQ' },
  { start: '00000', end: '99499', pos: '81', service_type: '5' },
];

// Rows with only a start and no end will only match the start code.
const CATEGORY_II_MAPPING: {
  start: string;
  end: string;
  service_type: string;
}[] = [
  { start: 'A4206', end: 'A4213', service_type: '1' },
  { start: 'A4216', end: 'A4218', service_type: '1' },
  { start: 'A4300', end: 'A4301', service_type: '1' },
  { start: 'A6010', end: 'A6011', service_type: '1' },
  { start: 'A6021', end: 'A6024', service_type: '1' },
  { start: 'A6154', end: '', service_type: '1' },
  { start: 'A6196', end: 'A6199', service_type: '1' },
  { start: 'A6203', end: 'A6214', service_type: '1' },
  { start: 'A6216', end: 'A6224', service_type: '1' },
  { start: 'A6228', end: 'A6248', service_type: '1' },
  { start: 'A6251', end: 'A6259', service_type: '1' },
  { start: 'A6261', end: 'A6262', service_type: '1' },
  { start: 'A6266', end: '', service_type: '1' },
  { start: 'A6403', end: 'A6404', service_type: '1' },
  { start: 'A6407', end: '', service_type: '1' },
  { start: 'A6410', end: 'A6412', service_type: '1' },
  { start: 'A6441', end: 'A6457', service_type: '1' },
  { start: 'A6460', end: 'A6461', service_type: '1' },
  { start: 'A9581', end: '', service_type: '1' },
  { start: 'B4034', end: 'B4036', service_type: '1' },
  { start: 'B4081', end: 'B4083', service_type: '1' },
  { start: 'B4087', end: 'B4088', service_type: '1' },
  { start: 'B4100', end: '', service_type: '1' },
  { start: 'B4102', end: '', service_type: '1' },
  { start: 'B4103', end: 'B4105', service_type: '1' },
  { start: 'B4149', end: 'B4150', service_type: '1' },
  { start: 'B4152', end: 'B4155', service_type: '1' },
  { start: 'B4157', end: 'B4162', service_type: '1' },
  { start: 'B4164', end: '', service_type: '1' },
  { start: 'B4168', end: '', service_type: '1' },
  { start: 'B4172', end: '', service_type: '1' },
  { start: 'B4176', end: '', service_type: '1' },
  { start: 'B4178', end: '', service_type: '1' },
  { start: 'B4180', end: '', service_type: '1' },
  { start: 'B4185', end: '', service_type: '1' },
  { start: 'B4187', end: '', service_type: '1' },
  { start: 'B4189', end: '', service_type: '1' },
  { start: 'B4193', end: '', service_type: '1' },
  { start: 'B4197', end: '', service_type: '1' },
  { start: 'B4199', end: '', service_type: '1' },
  { start: 'B4216', end: '', service_type: '1' },
  { start: 'B4220', end: '', service_type: '1' },
  { start: 'B4222', end: '', service_type: '1' },
  { start: 'B4224', end: '', service_type: '1' },
  { start: 'B5000', end: '', service_type: '1' },
  { start: 'B5100', end: '', service_type: '1' },
  { start: 'B5200', end: '', service_type: '1' },
  { start: 'B9998', end: '', service_type: '1' },
  { start: 'B9999', end: '', service_type: '1' },
  { start: 'C8957', end: '', service_type: '1' },
  { start: 'C9121', end: '', service_type: '1' },
  { start: 'C9132', end: '', service_type: '1' },
  { start: 'C9248', end: '', service_type: '1' },
  { start: 'C9285', end: '', service_type: '1' },
  { start: 'C9290', end: '', service_type: '1' },
  { start: 'C9293', end: '', service_type: '1' },
  { start: 'C9399', end: '', service_type: '1' },
  { start: 'C9460', end: '', service_type: '1' },
  { start: 'C9738', end: '', service_type: '1' },
  { start: 'C9898', end: '', service_type: '1' },
  { start: 'G0108', end: 'G0109', service_type: '1' },
  { start: 'G0166', end: 'G0168', service_type: '1' },
  { start: 'G0235', end: 'G0247', service_type: '1' },
  { start: 'G0259', end: 'G0269', service_type: '1' },
  { start: 'G0270', end: 'G0295', service_type: '1' },
  { start: 'G0302', end: 'G0305', service_type: '1' },
  { start: 'G0329', end: 'G0340', service_type: '1' },
  { start: 'G0372', end: '', service_type: '1' },
  { start: 'G0420', end: 'G0421', service_type: '1' },
  { start: 'G0459', end: '', service_type: '1' },
  { start: 'G0466', end: 'G0467', service_type: '1' },
  { start: 'G0473', end: '', service_type: '1' },
  { start: 'G0501', end: '', service_type: '1' },
  { start: 'G0505', end: '', service_type: '1' },
  { start: 'G0513', end: 'G0518', service_type: '1' },
  { start: 'G0908', end: 'G0910', service_type: '1' },
  { start: 'G0920', end: 'G0922', service_type: '1' },
  { start: 'G1000', end: 'G1023', service_type: '1' },
  { start: 'G2000', end: '', service_type: '1' },
  { start: 'G2010', end: '', service_type: '1' },
  { start: 'G2020', end: 'G2025', service_type: '1' },
  { start: 'G2089', end: 'G2101', service_type: '1' },
  { start: 'G2105', end: 'G2120', service_type: '1' },
  { start: 'G2123', end: 'G2167', service_type: '1' },
  { start: 'G2173', end: 'G2175', service_type: '1' },
  { start: 'G2177', end: 'G2195', service_type: '1' },
  { start: 'G2204', end: '', service_type: '1' },
  { start: 'G2209', end: 'G2212', service_type: '1' },
  { start: 'G8399', end: 'G8406', service_type: '1' },
  { start: 'G8417', end: 'G8430', service_type: '1' },
  { start: 'G8442', end: 'G8461', service_type: '1' },
  { start: 'G8473', end: 'G8478', service_type: '1' },
  { start: 'G8485', end: 'G8509', service_type: '1' },
  { start: 'G8530', end: 'G8552', service_type: '1' },
  { start: 'G8579', end: 'G8602', service_type: '1' },
  { start: 'G8629', end: 'G8635', service_type: '1' },
  { start: 'G8645', end: 'G8646', service_type: '1' },
  { start: 'G8682', end: 'G8698', service_type: '1' },
  { start: 'G8704', end: 'G8708', service_type: '1' },
  { start: 'G8709', end: 'G8713', service_type: '1' },
  { start: 'G8725', end: 'G8728', service_type: '1' },
  { start: 'G8730', end: 'G8737', service_type: '1' },
  { start: 'G8751', end: 'G8785', service_type: '1' },
  { start: 'G8809', end: 'G8811', service_type: '1' },
  { start: 'G8815', end: 'G8817', service_type: '1' },
  { start: 'G8839', end: 'G8846', service_type: '1' },
  { start: 'G8848', end: 'G8855', service_type: '1' },
  { start: 'G8856', end: 'G8863', service_type: '1' },
  { start: 'G8868', end: 'G8869', service_type: '1' },
  { start: 'G8871', end: 'G8874', service_type: '1' },
  { start: 'G8886', end: 'G8906', service_type: '1' },
  { start: 'G8912', end: 'G8913', service_type: '1' },
  { start: 'G8935', end: 'G8943', service_type: '1' },
  { start: 'G8949', end: 'G8955', service_type: '1' },
  { start: 'G8958', end: '', service_type: '1' },
  { start: 'G8973', end: 'G8974', service_type: '1' },
  { start: 'G8978', end: 'G8999', service_type: '1' },
  { start: 'G9013', end: 'G9014', service_type: '1' },
  { start: 'G9017', end: 'G9036', service_type: '1' },
  { start: 'G9140', end: '', service_type: '1' },
  { start: 'G9143', end: '', service_type: '1' },
  { start: 'G9148', end: 'G9153', service_type: '1' },
  { start: 'G9156', end: '', service_type: '1' },
  { start: 'G9158', end: 'G9186', service_type: '1' },
  { start: 'G9188', end: 'G9192', service_type: '1' },
  { start: 'G9196', end: 'G9211', service_type: '1' },
  { start: 'G9214', end: 'G9232', service_type: '1' },
  { start: 'G9236', end: '', service_type: '1' },
  { start: 'G9238', end: '', service_type: '1' },
  { start: 'G9242', end: 'G9263', service_type: '1' },
  { start: 'G9267', end: 'G9278', service_type: '1' },
  { start: 'G9280', end: 'G9281', service_type: '1' },
  { start: 'G9298', end: 'G9302', service_type: '1' },
  { start: 'G9305', end: 'G9307', service_type: '1' },
  { start: 'G9309', end: '', service_type: '1' },
  { start: 'G9311', end: 'G9329', service_type: '1' },
  { start: 'G9340', end: 'G9347', service_type: '1' },
  { start: 'G9359', end: 'G9360', service_type: '1' },
  { start: 'G9364', end: 'G9370', service_type: '1' },
  { start: 'G9949', end: '', service_type: '1' },
  { start: 'K0744', end: 'K0746', service_type: '1' },
  { start: 'Q4001', end: 'Q4051', service_type: '1' },
  { start: 'Q9994', end: '', service_type: '1' },
  { start: 'S0260', end: '', service_type: '1' },
  { start: 'A4206', end: 'A4213', service_type: '2' },
  { start: 'A4300', end: 'A4301', service_type: '2' },
  { start: 'A4461', end: 'A4463', service_type: '2' },
  { start: 'A4461', end: '', service_type: '2' },
  { start: 'A4463', end: '', service_type: '2' },
  { start: 'A6010', end: 'A6011', service_type: '2' },
  { start: 'A6021', end: 'A6024', service_type: '2' },
  { start: 'A6154', end: '', service_type: '2' },
  { start: 'A6196', end: 'A6199', service_type: '2' },
  { start: 'A6203', end: 'A6214', service_type: '2' },
  { start: 'A6216', end: 'A6224', service_type: '2' },
  { start: 'A6228', end: 'A6248', service_type: '2' },
  { start: 'A6251', end: 'A6259', service_type: '2' },
  { start: 'A6261', end: 'A6262', service_type: '2' },
  { start: 'A6266', end: '', service_type: '2' },
  { start: 'A6403', end: 'A6404', service_type: '2' },
  { start: 'A6407', end: '', service_type: '2' },
  { start: 'A6410', end: 'A6412', service_type: '2' },
  { start: 'A6441', end: 'A6457', service_type: '2' },
  { start: 'A6460', end: 'A6461', service_type: '2' },
  { start: 'A6501', end: 'A6512', service_type: '2' },
  { start: 'C5271', end: 'C5278', service_type: '2' },
  { start: 'C9600', end: 'C9608', service_type: '2' },
  { start: 'C9724', end: '', service_type: '2' },
  { start: 'C9727', end: 'C9728', service_type: '2' },
  { start: 'C9734', end: 'C9735', service_type: '2' },
  { start: 'C9737', end: '', service_type: '2' },
  { start: 'C9739', end: 'C9743', service_type: '2' },
  { start: 'C9745', end: 'C9755', service_type: '2' },
  { start: 'C9757', end: '', service_type: '2' },
  { start: 'C9758', end: 'C9761', service_type: '2' },
  { start: 'C9764', end: 'C9778', service_type: '2' },
  { start: 'G0127', end: '', service_type: '2' },
  { start: 'G0173', end: '', service_type: '2' },
  { start: 'G0186', end: '', service_type: '2' },
  { start: 'G0268', end: '', service_type: '2' },
  { start: 'G0278', end: '', service_type: '2' },
  { start: 'G0289', end: '', service_type: '2' },
  { start: 'G0293', end: 'G0294', service_type: '2' },
  { start: 'G0341', end: 'G0343', service_type: '2' },
  { start: 'G0364', end: '', service_type: '2' },
  { start: 'G0412', end: 'G0415', service_type: '2' },
  { start: 'G0428', end: '', service_type: '2' },
  { start: 'G0448', end: '', service_type: '2' },
  { start: 'G2170', end: 'G2171', service_type: '2' },
  { start: 'G6018', end: 'G6025', service_type: '2' },
  { start: 'G6027', end: 'G6028', service_type: '2' },
  { start: 'G8569', end: 'G8578', service_type: '2' },
  { start: 'G8627', end: 'G8628', service_type: '2' },
  { start: 'G8702', end: 'G8703', service_type: '2' },
  { start: 'G8916', end: 'G8918', service_type: '2' },
  { start: 'G9235', end: '', service_type: '2' },
  { start: 'G9237', end: '', service_type: '2' },
  { start: 'G9303', end: 'G9304', service_type: '2' },
  { start: 'G9308', end: '', service_type: '2' },
  { start: 'K0744', end: 'K0746', service_type: '2' },
  { start: 'M0301', end: '', service_type: '2' },
  { start: 'Q4001', end: 'Q4051', service_type: '2' },
  { start: 'S0810', end: '', service_type: '2' },
  { start: 'S2053', end: '', service_type: '2' },
  { start: 'S2060', end: 'S2061', service_type: '2' },
  { start: 'S2065', end: 'S2067', service_type: '2' },
  { start: 'S2068', end: '', service_type: '2' },
  { start: 'S2112', end: '', service_type: '2' },
  { start: 'S2115', end: '', service_type: '2' },
  { start: 'S2205', end: 'S2209', service_type: '2' },
  { start: 'S2225', end: '', service_type: '2' },
  { start: 'S2230', end: '', service_type: '2' },
  { start: 'S2235', end: '', service_type: '2' },
  { start: 'S2300', end: '', service_type: '2' },
  { start: 'S2350', end: 'S2351', service_type: '2' },
  { start: 'S2360', end: 'S2361', service_type: '2' },
  { start: 'S2400', end: 'S2404', service_type: '2' },
  { start: 'S2405', end: '', service_type: '2' },
  { start: 'S2409', end: '', service_type: '2' },
  { start: 'S2411', end: '', service_type: '2' },
  { start: 'S2900', end: '', service_type: '2' },
  { start: 'S4981', end: '', service_type: '2' },
  { start: 'S8490', end: '', service_type: '2' },
  { start: 'G0296', end: '', service_type: '3' },
  { start: 'G0406', end: 'G0408', service_type: '3' },
  { start: 'G0425', end: 'G0427', service_type: '3' },
  { start: 'G0508', end: 'G0509', service_type: '3' },
  { start: 'Q9001', end: 'Q9003', service_type: '3' },
  { start: 'G0130', end: '', service_type: '4' },
  { start: 'Q0092', end: '', service_type: '4' },
  { start: 'G0306', end: 'G0307', service_type: '5' },
  { start: 'G0398', end: 'G0400', service_type: '5' },
  { start: 'G0403', end: 'G0405', service_type: '5' },
  { start: 'G0416', end: 'G0419', service_type: '5' },
  { start: 'G0431', end: '', service_type: '5' },
  { start: 'G0434', end: '', service_type: '5' },
  { start: 'G0461', end: 'G0462', service_type: '5' },
  { start: 'G0471', end: 'G0472', service_type: '5' },
  { start: 'G0475', end: 'G0483', service_type: '5' },
  { start: 'G0499', end: '', service_type: '5' },
  { start: 'G0659', end: '', service_type: '5' },
  { start: 'G2023', end: 'G2024', service_type: '5' },
  { start: 'G6030', end: 'G6032', service_type: '5' },
  { start: 'G6034', end: 'G6058', service_type: '5' },
  { start: 'P2028', end: 'P2029', service_type: '5' },
  { start: 'P2031', end: '', service_type: '5' },
  { start: 'P2033', end: '', service_type: '5' },
  { start: 'P2038', end: '', service_type: '5' },
  { start: 'P7001', end: '', service_type: '5' },
  { start: 'P9100', end: '', service_type: '5' },
  { start: 'P9615', end: '', service_type: '5' },
  { start: 'Q0035', end: '', service_type: '5' },
  { start: 'Q0111', end: 'Q0114', service_type: '5' },
  { start: 'Q3031', end: '', service_type: '5' },
  { start: 'Q9987', end: '', service_type: '5' },
  { start: 'S9529', end: '', service_type: '5' },
  { start: 'U0001', end: 'U0005', service_type: '5' },
  { start: 'A9513', end: '', service_type: '6' },
  { start: 'A9517', end: '', service_type: '6' },
  { start: 'A9530', end: '', service_type: '6' },
  { start: 'A9532', end: '', service_type: '6' },
  { start: 'A9543', end: '', service_type: '6' },
  { start: 'A9545', end: '', service_type: '6' },
  { start: 'A9563', end: 'A9564', service_type: '6' },
  { start: 'A9600', end: '', service_type: '6' },
  { start: 'A9604', end: '', service_type: '6' },
  { start: 'A9699', end: '', service_type: '6' },
  { start: 'C1716', end: 'C1717', service_type: '6' },
  { start: 'C1719', end: '', service_type: '6' },
  { start: 'C2616', end: '', service_type: '6' },
  { start: 'C2638', end: '', service_type: '6' },
  { start: 'C2644', end: '', service_type: '6' },
  { start: 'C9725', end: '', service_type: '6' },
  { start: 'C9726', end: '', service_type: '6' },
  { start: 'G0458', end: '', service_type: '6' },
  { start: 'G6001', end: 'G6017', service_type: '6' },
  { start: 'G0500', end: '', service_type: '7' },
  { start: 'G9362', end: 'G9363', service_type: '7' },
  { start: 'A4250', end: '', service_type: '9' },
  { start: 'A4262', end: 'A4263', service_type: '9' },
  { start: 'A4265', end: '', service_type: '9' },
  { start: 'A4270', end: '', service_type: '9' },
  { start: 'A4305', end: 'A4306', service_type: '9' },
  { start: 'A4360', end: '', service_type: '9' },
  { start: 'A4458', end: 'A4459', service_type: '9' },
  { start: 'A4465', end: 'A4467', service_type: '9' },
  { start: 'A4520', end: '', service_type: '9' },
  { start: 'A4550', end: '', service_type: '9' },
  { start: 'A4553', end: 'A4554', service_type: '9' },
  { start: 'A4566', end: '', service_type: '9' },
  { start: 'A4575', end: '', service_type: '9' },
  { start: 'A4580', end: '', service_type: '9' },
  { start: 'A4590', end: '', service_type: '9' },
  { start: 'A4634', end: '', service_type: '9' },
  { start: 'A4648', end: 'A4650', service_type: '9' },
  { start: 'A4932', end: '', service_type: '9' },
  { start: 'A6025', end: '', service_type: '9' },
  { start: 'A9150', end: '', service_type: '9' },
  { start: 'A9152', end: '', service_type: '9' },
  { start: 'A9153', end: '', service_type: '9' },
  { start: 'A9155', end: '', service_type: '9' },
  { start: 'A9180', end: '', service_type: '9' },
  { start: 'A9270', end: '', service_type: '9' },
  { start: 'A9272', end: '', service_type: '9' },
  { start: 'A9280', end: '', service_type: '9' },
  { start: 'A9282', end: '', service_type: '9' },
  { start: 'A9285', end: '', service_type: '9' },
  { start: 'A9286', end: '', service_type: '9' },
  { start: 'A9300', end: '', service_type: '9' },
  { start: 'A9700', end: '', service_type: '9' },
  { start: 'C1052', end: '', service_type: '9' },
  { start: 'C1062', end: '', service_type: '9' },
  { start: 'C1300', end: '', service_type: '9' },
  { start: 'C1713', end: 'C1715', service_type: '9' },
  { start: 'C1721', end: '', service_type: '9' },
  { start: 'C1722', end: '', service_type: '9' },
  { start: 'C1724', end: 'C1733', service_type: '9' },
  { start: 'C1748', end: 'C1763', service_type: '9' },
  { start: 'C1765', end: 'C1782', service_type: '9' },
  { start: 'C1787', end: 'C1788', service_type: '9' },
  { start: 'C1814', end: '', service_type: '9' },
  { start: 'C1816', end: 'C1825', service_type: '9' },
  { start: 'C1830', end: '', service_type: '9' },
  { start: 'C1874', end: 'C1878', service_type: '9' },
  { start: 'C1880', end: '', service_type: '9' },
  { start: 'C1882', end: 'C1900', service_type: '9' },
  { start: 'C1982', end: '', service_type: '9' },
  { start: 'C2596', end: '', service_type: '9' },
  { start: 'C2613', end: 'C2615', service_type: '9' },
  { start: 'C2617', end: 'C2618', service_type: '9' },
  { start: 'C2623', end: 'C2636', service_type: '9' },
  { start: 'C2645', end: '', service_type: '9' },
  { start: 'C9014', end: 'C9016', service_type: '9' },
  { start: 'C9021', end: 'C9080', service_type: '9' },
  { start: 'C9113', end: '', service_type: '9' },
  { start: 'C9122', end: '', service_type: '9' },
  { start: 'C9133', end: 'C9141', service_type: '9' },
  { start: 'C9250', end: '', service_type: '9' },
  { start: 'C9254', end: '', service_type: '9' },
  { start: 'C9257', end: '', service_type: '9' },
  { start: 'C9349', end: '', service_type: '9' },
  { start: 'C9352', end: 'C9356', service_type: '9' },
  { start: 'C9358', end: 'C9364', service_type: '9' },
  { start: 'C9441', end: 'C9456', service_type: '9' },
  { start: 'C9462', end: 'C9494', service_type: '9' },
  { start: 'C9497', end: '', service_type: '9' },
  { start: 'C9803', end: '', service_type: '9' },
  { start: 'E0190', end: '', service_type: '9' },
  { start: 'E0350', end: '', service_type: '9' },
  { start: 'E0352', end: '', service_type: '9' },
  { start: 'E0445', end: 'E0446', service_type: '9' },
  { start: 'E0639', end: 'E0640', service_type: '9' },
  { start: 'E0749', end: '', service_type: '9' },
  { start: 'E0761', end: '', service_type: '9' },
  { start: 'E0769', end: '', service_type: '9' },
  { start: 'E0781', end: '', service_type: '9' },
  { start: 'E0786', end: '', service_type: '9' },
  { start: 'E8000', end: 'E8002', service_type: '9' },
  { start: 'G0260', end: '', service_type: '9' },
  { start: 'G0333', end: '', service_type: '9' },
  { start: 'G0432', end: 'G0433', service_type: '9' },
  { start: 'G0435', end: '', service_type: '9' },
  { start: 'G2021', end: 'G2022', service_type: '9' },
  { start: 'H0033', end: 'H0034', service_type: '9' },
  { start: 'J0883', end: '', service_type: '9' },
  { start: 'J0885', end: '', service_type: '9' },
  { start: 'J0888', end: '', service_type: '9' },
  { start: 'J1095', end: '', service_type: '9' },
  { start: 'J3396', end: '', service_type: '9' },
  { start: 'J7309', end: 'J7310', service_type: '9' },
  { start: 'J7335', end: 'J7336', service_type: '9' },
  { start: 'J8562', end: '', service_type: '9' },
  { start: 'J8565', end: '', service_type: '9' },
  { start: 'K0462', end: '', service_type: '9' },
  { start: 'Q2026', end: '', service_type: '9' },
  { start: 'Q2028', end: '', service_type: '9' },
  { start: 'Q2052', end: '', service_type: '9' },
  { start: 'Q3014', end: '', service_type: '9' },
  { start: 'Q5106', end: '', service_type: '9' },
  { start: 'Q9969', end: '', service_type: '9' },
  { start: 'Q9973', end: '', service_type: '9' },
  { start: 'S0088', end: '', service_type: '9' },
  { start: 'S0091', end: 'S0093', service_type: '9' },
  { start: 'S0109', end: '', service_type: '9' },
  { start: 'S0117', end: '', service_type: '9' },
  { start: 'S0119', end: '', service_type: '9' },
  { start: 'S0136', end: 'S0140', service_type: '9' },
  { start: 'S0142', end: '', service_type: '9' },
  { start: 'S0144', end: 'S0145', service_type: '9' },
  { start: 'S0148', end: '', service_type: '9' },
  { start: 'S0155', end: 'S0157', service_type: '9' },
  { start: 'S0160', end: '', service_type: '9' },
  { start: 'S0164', end: '', service_type: '9' },
  { start: 'S0166', end: '', service_type: '9' },
  { start: 'S0169', end: '', service_type: '9' },
  { start: 'S0171', end: '', service_type: '9' },
  { start: 'S0172', end: '', service_type: '9' },
  { start: 'S0174', end: 'S0178', service_type: '9' },
  { start: 'S0182', end: '', service_type: '9' },
  { start: 'S0183', end: '', service_type: '9' },
  { start: 'S0187', end: '', service_type: '9' },
  { start: 'S0190', end: 'S0191', service_type: '9' },
  { start: 'S0194', end: '', service_type: '9' },
  { start: 'S0220', end: 'S0221', service_type: '9' },
  { start: 'S0250', end: '', service_type: '9' },
  { start: 'S0255', end: '', service_type: '9' },
  { start: 'S0257', end: '', service_type: '9' },
  { start: 'S0265', end: '', service_type: '9' },
  { start: 'S0285', end: '', service_type: '9' },
  { start: 'S0302', end: '', service_type: '9' },
  { start: 'S0400', end: '', service_type: '9' },
  { start: 'S0515', end: '', service_type: '9' },
  { start: 'S0601', end: '', service_type: '9' },
  { start: 'S0630', end: '', service_type: '9' },
  { start: 'S0800', end: '', service_type: '9' },
  { start: 'S1015', end: 'S1016', service_type: '9' },
  { start: 'S1090', end: '', service_type: '9' },
  { start: 'S1091', end: '', service_type: '9' },
  { start: 'S2054', end: 'S2055', service_type: '9' },
  { start: 'S2070', end: '', service_type: '9' },
  { start: 'S2079', end: '', service_type: '9' },
  { start: 'S2080', end: '', service_type: '9' },
  { start: 'S2083', end: '', service_type: '9' },
  { start: 'S2095', end: '', service_type: '9' },
  { start: 'S2102', end: 'S2103', service_type: '9' },
  { start: 'S2107', end: '', service_type: '9' },
  { start: 'S2117', end: 'S2118', service_type: '9' },
  { start: 'S2120', end: '', service_type: '9' },
  { start: 'S2140', end: '', service_type: '9' },
  { start: 'S2142', end: '', service_type: '9' },
  { start: 'S2150', end: '', service_type: '9' },
  { start: 'S2152', end: '', service_type: '9' },
  { start: 'S2202', end: '', service_type: '9' },
  { start: 'S2325', end: '', service_type: '9' },
  { start: 'S2340', end: 'S2342', service_type: '9' },
  { start: 'S2348', end: '', service_type: '9' },
  { start: 'S3000', end: '', service_type: '9' },
  { start: 'S3600', end: 'S3601', service_type: '9' },
  { start: 'S3630', end: '', service_type: '9' },
  { start: 'S3645', end: '', service_type: '9' },
  { start: 'S3650', end: '', service_type: '9' },
  { start: 'S3652', end: '', service_type: '9' },
  { start: 'S3655', end: '', service_type: '9' },
  { start: 'S3708', end: '', service_type: '9' },
  { start: 'S3722', end: '', service_type: '9' },
  { start: 'S3800', end: '', service_type: '9' },
  { start: 'S3840', end: 'S3842', service_type: '9' },
  { start: 'S3844', end: 'S3846', service_type: '9' },
  { start: 'S3849', end: 'S3850', service_type: '9' },
  { start: 'S3852', end: 'S3855', service_type: '9' },
  { start: 'S3861', end: '', service_type: '9' },
  { start: 'S3865', end: 'S3866', service_type: '9' },
  { start: 'S3870', end: '', service_type: '9' },
  { start: 'S3900', end: '', service_type: '9' },
  { start: 'S3902', end: '', service_type: '9' },
  { start: 'S3904', end: '', service_type: '9' },
  { start: 'S4005', end: '', service_type: '9' },
  { start: 'S5010', end: 'S5014', service_type: '9' },
  { start: 'S5100', end: 'S5102', service_type: '9' },
  { start: 'S5101', end: '', service_type: '9' },
  { start: 'S5105', end: '', service_type: '9' },
  { start: 'S5125', end: 'S5126', service_type: '9' },
  { start: 'S5135', end: 'S5136', service_type: '9' },
  { start: 'S8030', end: '', service_type: '9' },
  { start: 'S8035', end: '', service_type: '9' },
  { start: 'S8040', end: '', service_type: '9' },
  { start: 'S8055', end: '', service_type: '9' },
  { start: 'S8080', end: '', service_type: '9' },
  { start: 'S8085', end: '', service_type: '9' },
  { start: 'S8092', end: '', service_type: '9' },
  { start: 'S8096', end: '', service_type: '9' },
  { start: 'S8130', end: 'S8131', service_type: '9' },
  { start: 'S8185', end: 'S8186', service_type: '9' },
  { start: 'S8189', end: '', service_type: '9' },
  { start: 'S8210', end: '', service_type: '9' },
  { start: 'S8262', end: '', service_type: '9' },
  { start: 'S8265', end: '', service_type: '9' },
  { start: 'S8270', end: '', service_type: '9' },
  { start: 'S8301', end: '', service_type: '9' },
  { start: 'S8420', end: 'S8431', service_type: '9' },
  { start: 'S8460', end: '', service_type: '9' },
  { start: 'S8930', end: '', service_type: '9' },
  { start: 'S8940', end: '', service_type: '9' },
  { start: 'S8948', end: '', service_type: '9' },
  { start: 'S8950', end: '', service_type: '9' },
  { start: 'S8990', end: '', service_type: '9' },
  { start: 'S8999', end: '', service_type: '9' },
  { start: 'S9001', end: '', service_type: '9' },
  { start: 'S9007', end: '', service_type: '9' },
  { start: 'S9015', end: '', service_type: '9' },
  { start: 'S9024', end: 'S9025', service_type: '9' },
  { start: 'S9034', end: '', service_type: '9' },
  { start: 'S9055', end: 'S9056', service_type: '9' },
  { start: 'S9090', end: '', service_type: '9' },
  { start: 'S9117', end: '', service_type: '9' },
  { start: 'S9401', end: '', service_type: '9' },
  { start: 'S9433', end: 'S9435', service_type: '9' },
  { start: 'S9445', end: 'S9446', service_type: '9' },
  { start: 'S9449', end: '', service_type: '9' },
  { start: 'S9451', end: 'S9452', service_type: '9' },
  { start: 'S9454', end: 'S9455', service_type: '9' },
  { start: 'S9460', end: '', service_type: '9' },
  { start: 'S9465', end: '', service_type: '9' },
  { start: 'S9470', end: '', service_type: '9' },
  { start: 'S9474', end: '', service_type: '9' },
  { start: 'S9490', end: '', service_type: '9' },
  { start: 'S9494', end: '', service_type: '9' },
  { start: 'S9497', end: '', service_type: '9' },
  { start: 'S9500', end: 'S9504', service_type: '9' },
  { start: 'S9537', end: 'S9538', service_type: '9' },
  { start: 'S9542', end: '', service_type: '9' },
  { start: 'S9558', end: 'S9560', service_type: '9' },
  { start: 'S9562', end: '', service_type: '9' },
  { start: 'S9590', end: '', service_type: '9' },
  { start: 'S9810', end: '', service_type: '9' },
  { start: 'S9900', end: '', service_type: '9' },
  { start: 'S9901', end: '', service_type: '9' },
  { start: 'S9970', end: '', service_type: '9' },
  { start: 'S9975', end: 'S9977', service_type: '9' },
  { start: 'S9981', end: '', service_type: '9' },
  { start: 'S9982', end: '', service_type: '9' },
  { start: 'S9986', end: '', service_type: '9' },
  { start: 'S9988', end: 'S9992', service_type: '9' },
  { start: 'S9994', end: '', service_type: '9' },
  { start: 'S9996', end: '', service_type: '9' },
  { start: 'S9999', end: '', service_type: '9' },
  { start: 'T1001', end: '', service_type: '9' },
  { start: 'T1004', end: '', service_type: '9' },
  { start: 'T1013', end: 'T1014', service_type: '9' },
  { start: 'T1019', end: 'T1020', service_type: '9' },
  { start: 'T1023', end: 'T1026', service_type: '9' },
  { start: 'T1502', end: 'T1503', service_type: '9' },
  { start: 'T1505', end: '', service_type: '9' },
  { start: 'T1999', end: '', service_type: '9' },
  { start: 'T2012', end: 'T2021', service_type: '9' },
  { start: 'T2025', end: 'T2028', service_type: '9' },
  { start: 'T2036', end: 'T2041', service_type: '9' },
  { start: 'T2047', end: '', service_type: '9' },
  { start: 'T4521', end: 'T4545', service_type: '9' },
  { start: 'T5999', end: '', service_type: '9' },
  { start: 'G0460', end: '', service_type: '10' },
  { start: 'P9010', end: 'P9011', service_type: '10' },
  { start: 'P9016', end: '', service_type: '10' },
  { start: 'P9017', end: '', service_type: '10' },
  { start: 'P9019', end: 'P9020', service_type: '10' },
  { start: 'P9021', end: 'P9022', service_type: '10' },
  { start: 'P9023', end: '', service_type: '10' },
  { start: 'P9031', end: 'P9037', service_type: '10' },
  { start: 'P9038', end: 'P9040', service_type: '10' },
  { start: 'P9039', end: '', service_type: '10' },
  { start: 'P9041', end: '', service_type: '10' },
  { start: 'P9043', end: 'P9048', service_type: '10' },
  { start: 'P9050', end: '', service_type: '10' },
  { start: 'P9051', end: '', service_type: '10' },
  { start: 'P9052', end: '', service_type: '10' },
  { start: 'P9053', end: '', service_type: '10' },
  { start: 'P9054', end: '', service_type: '10' },
  { start: 'P9055', end: '', service_type: '10' },
  { start: 'P9056', end: 'P9058', service_type: '10' },
  { start: 'P9057', end: '', service_type: '10' },
  { start: 'P9059', end: 'P9060', service_type: '10' },
  { start: 'P9070', end: 'P9073', service_type: '10' },
  { start: 'P9099', end: '', service_type: '10' },
  { start: 'Q9988', end: '', service_type: '10' },
  { start: 'A4618', end: '', service_type: '11' },
  { start: 'A4628', end: '', service_type: '11' },
  { start: 'A4630', end: '', service_type: '11' },
  { start: 'A4633', end: '', service_type: '11' },
  { start: 'A4635', end: 'A4637', service_type: '11' },
  { start: 'A4639', end: 'A4640', service_type: '11' },
  { start: 'A7000', end: 'A7002', service_type: '11' },
  { start: 'A7005', end: 'A7006', service_type: '11' },
  { start: 'A7009', end: '', service_type: '11' },
  { start: 'A7012', end: '', service_type: '11' },
  { start: 'A7014', end: 'A7017', service_type: '11' },
  { start: 'A7025', end: 'A7039', service_type: '11' },
  { start: 'A7044', end: 'A7045', service_type: '11' },
  { start: 'A7047', end: '', service_type: '11' },
  { start: 'A8000', end: 'A8004', service_type: '11' },
  { start: 'A9273', end: '', service_type: '11' },
  { start: 'A9281', end: '', service_type: '11' },
  { start: 'A9284', end: '', service_type: '11' },
  { start: 'B9000', end: '', service_type: '11' },
  { start: 'B9002', end: '', service_type: '11' },
  { start: 'B9004', end: '', service_type: '11' },
  { start: 'B9006', end: '', service_type: '11' },
  { start: 'E0100', end: '', service_type: '11' },
  { start: 'E0105', end: '', service_type: '11' },
  { start: 'E0110', end: 'E0114', service_type: '11' },
  { start: 'E0116', end: 'E0118', service_type: '11' },
  { start: 'E0130', end: '', service_type: '11' },
  { start: 'E0135', end: '', service_type: '11' },
  { start: 'E0140', end: '', service_type: '11' },
  { start: 'E0141', end: '', service_type: '11' },
  { start: 'E0143', end: '', service_type: '11' },
  { start: 'E0144', end: '', service_type: '11' },
  { start: 'E0147', end: 'E0149', service_type: '11' },
  { start: 'E0153', end: 'E0163', service_type: '11' },
  { start: 'E0167', end: 'E0168', service_type: '11' },
  { start: 'E0170', end: 'E0172', service_type: '11' },
  { start: 'E0175', end: '', service_type: '11' },
  { start: 'E0184', end: 'E0185', service_type: '11' },
  { start: 'E0188', end: 'E0189', service_type: '11' },
  { start: 'E0191', end: '', service_type: '11' },
  { start: 'E0197', end: 'E0200', service_type: '11' },
  { start: 'E0205', end: '', service_type: '11' },
  { start: 'E0210', end: '', service_type: '11' },
  { start: 'E0215', end: '', service_type: '11' },
  { start: 'E0217', end: '', service_type: '11' },
  { start: 'E0218', end: '', service_type: '11' },
  { start: 'E0221', end: '', service_type: '11' },
  { start: 'E0225', end: '', service_type: '11' },
  { start: 'E0239', end: '', service_type: '11' },
  { start: 'E0241', end: 'E0249', service_type: '11' },
  { start: 'E0271', end: 'E0276', service_type: '11' },
  { start: 'E0280', end: '', service_type: '11' },
  { start: 'E0300', end: '', service_type: '11' },
  { start: 'E0310', end: '', service_type: '11' },
  { start: 'E0315', end: 'E0316', service_type: '11' },
  { start: 'E0325', end: 'E0326', service_type: '11' },
  { start: 'E0370', end: 'E0373', service_type: '11' },
  { start: 'E0457', end: '', service_type: '11' },
  { start: 'E0481', end: '', service_type: '11' },
  { start: 'E0484', end: '', service_type: '11' },
  { start: 'E0487', end: '', service_type: '11' },
  { start: 'E0560', end: 'E0562', service_type: '11' },
  { start: 'E0572', end: '', service_type: '11' },
  { start: 'E0574', end: '', service_type: '11' },
  { start: 'E0605', end: '', service_type: '11' },
  { start: 'E0607', end: '', service_type: '11' },
  { start: 'E0610', end: '', service_type: '11' },
  { start: 'E0615', end: '', service_type: '11' },
  { start: 'E0620', end: 'E0621', service_type: '11' },
  { start: 'E0625', end: '', service_type: '11' },
  { start: 'E0627', end: 'E0629', service_type: '11' },
  { start: 'E0637', end: 'E0638', service_type: '11' },
  { start: 'E0641', end: 'E0642', service_type: '11' },
  { start: 'E0650', end: 'E0652', service_type: '11' },
  { start: 'E0655', end: 'E0657', service_type: '11' },
  { start: 'E0660', end: '', service_type: '11' },
  { start: 'E0665', end: 'E0673', service_type: '11' },
  { start: 'E0676', end: '', service_type: '11' },
  { start: 'E0691', end: 'E0694', service_type: '11' },
  { start: 'E0700', end: '', service_type: '11' },
  { start: 'E0705', end: '', service_type: '11' },
  { start: 'E0710', end: '', service_type: '11' },
  { start: 'E0720', end: '', service_type: '11' },
  { start: 'E0730', end: 'E0731', service_type: '11' },
  { start: 'E0740', end: '', service_type: '11' },
  { start: 'E0746', end: 'E0748', service_type: '11' },
  { start: 'E0755', end: '', service_type: '11' },
  { start: 'E0760', end: '', service_type: '11' },
  { start: 'E0762', end: '', service_type: '11' },
  { start: 'E0764', end: '', service_type: '11' },
  { start: 'E0765', end: '', service_type: '11' },
  { start: 'E0770', end: '', service_type: '11' },
  { start: 'E0776', end: '', service_type: '11' },
  { start: 'E0779', end: '', service_type: '11' },
  { start: 'E0780', end: '', service_type: '11' },
  { start: 'E0782', end: '', service_type: '11' },
  { start: 'E0783', end: '', service_type: '11' },
  { start: 'E0840', end: '', service_type: '11' },
  { start: 'E0849', end: '', service_type: '11' },
  { start: 'E0850', end: '', service_type: '11' },
  { start: 'E0855', end: '', service_type: '11' },
  { start: 'E0856', end: '', service_type: '11' },
  { start: 'E0860', end: '', service_type: '11' },
  { start: 'E0870', end: '', service_type: '11' },
  { start: 'E0880', end: '', service_type: '11' },
  { start: 'E0890', end: '', service_type: '11' },
  { start: 'E0900', end: '', service_type: '11' },
  { start: 'E0942', end: '', service_type: '11' },
  { start: 'E0944', end: 'E0945', service_type: '11' },
  { start: 'E0947', end: 'E0948', service_type: '11' },
  { start: 'E0950', end: 'E0961', service_type: '11' },
  { start: 'E0966', end: 'E0967', service_type: '11' },
  { start: 'E0969', end: 'E0971', service_type: '11' },
  { start: 'E0973', end: 'E0974', service_type: '11' },
  { start: 'E0978', end: '', service_type: '11' },
  { start: 'E0980', end: 'E0982', service_type: '11' },
  { start: 'E0984', end: 'E0986', service_type: '11' },
  { start: 'E0990', end: '', service_type: '11' },
  { start: 'E0992', end: '', service_type: '11' },
  { start: 'E0994', end: '', service_type: '11' },
  { start: 'E0995', end: '', service_type: '11' },
  { start: 'E1002', end: 'E1012', service_type: '11' },
  { start: 'E1014', end: 'E1018', service_type: '11' },
  { start: 'E1020', end: '', service_type: '11' },
  { start: 'E1028', end: 'E1030', service_type: '11' },
  { start: 'E1161', end: '', service_type: '11' },
  { start: 'E1226', end: 'E1227', service_type: '11' },
  { start: 'E1229', end: 'E1239', service_type: '11' },
  { start: 'E1296', end: 'E1298', service_type: '11' },
  { start: 'E1300', end: '', service_type: '11' },
  { start: 'E1310', end: '', service_type: '11' },
  { start: 'E1354', end: '', service_type: '11' },
  { start: 'E1356', end: 'E1358', service_type: '11' },
  { start: 'E1372', end: '', service_type: '11' },
  { start: 'E1399', end: '', service_type: '11' },
  { start: 'E1700', end: '', service_type: '11' },
  { start: 'E1902', end: '', service_type: '11' },
  { start: 'E2100', end: 'E2101', service_type: '11' },
  { start: 'E2201', end: 'E2222', service_type: '11' },
  { start: 'E2224', end: 'E2228', service_type: '11' },
  { start: 'E2230', end: 'E2231', service_type: '11' },
  { start: 'E2291', end: 'E2295', service_type: '11' },
  { start: 'E2300', end: 'E2301', service_type: '11' },
  { start: 'E2310', end: 'E2313', service_type: '11' },
  { start: 'E2321', end: 'E2331', service_type: '11' },
  { start: 'E2340', end: 'E2343', service_type: '11' },
  { start: 'E2351', end: '', service_type: '11' },
  { start: 'E2358', end: 'E2378', service_type: '11' },
  { start: 'E2381', end: 'E2396', service_type: '11' },
  { start: 'E2500', end: '', service_type: '11' },
  { start: 'E2502', end: '', service_type: '11' },
  { start: 'E2504', end: '', service_type: '11' },
  { start: 'E2506', end: '', service_type: '11' },
  { start: 'E2508', end: '', service_type: '11' },
  { start: 'E2510', end: 'E2512', service_type: '11' },
  { start: 'E2599', end: '', service_type: '11' },
  { start: 'E2601', end: 'E2633', service_type: '11' },
  { start: 'K0005', end: '', service_type: '11' },
  { start: 'K0008', end: 'K0015', service_type: '11' },
  { start: 'K0017', end: 'K0020', service_type: '11' },
  { start: 'K0037', end: 'K0047', service_type: '11' },
  { start: 'K0050', end: 'K0053', service_type: '11' },
  { start: 'K0056', end: '', service_type: '11' },
  { start: 'K0065', end: '', service_type: '11' },
  { start: 'K0069', end: 'K0073', service_type: '11' },
  { start: 'K0077', end: '', service_type: '11' },
  { start: 'K0098', end: '', service_type: '11' },
  { start: 'K0105', end: '', service_type: '11' },
  { start: 'K0108', end: '', service_type: '11' },
  { start: 'K0554', end: '', service_type: '11' },
  { start: 'K0601', end: 'K0608', service_type: '11' },
  { start: 'K0669', end: '', service_type: '11' },
  { start: 'K0733', end: '', service_type: '11' },
  { start: 'K0800', end: 'K0802', service_type: '11' },
  { start: 'K0806', end: 'K0808', service_type: '11' },
  { start: 'K0812', end: 'K0816', service_type: '11' },
  { start: 'K0820', end: 'K0831', service_type: '11' },
  { start: 'K0835', end: 'K0843', service_type: '11' },
  { start: 'K0848', end: 'K0864', service_type: '11' },
  { start: 'K0868', end: 'K0871', service_type: '11' },
  { start: 'K0877', end: 'K0880', service_type: '11' },
  { start: 'K0884', end: 'K0886', service_type: '11' },
  { start: 'K0890', end: 'K0891', service_type: '11' },
  { start: 'K0898', end: 'K0900', service_type: '11' },
  { start: 'A4220', end: '', service_type: '12' },
  { start: 'A4221', end: '', service_type: '12' },
  { start: 'A4222', end: '', service_type: '12' },
  { start: 'A4223', end: '', service_type: '12' },
  { start: 'A4256', end: 'A4259', service_type: '12' },
  { start: 'A4280', end: '', service_type: '12' },
  { start: 'A4310', end: 'A4316', service_type: '12' },
  { start: 'A4320', end: 'A4322', service_type: '12' },
  { start: 'A4326', end: 'A4328', service_type: '12' },
  { start: 'A4330', end: 'A4338', service_type: '12' },
  { start: 'A4340', end: '', service_type: '12' },
  { start: 'A4344', end: '', service_type: '12' },
  { start: 'A4346', end: '', service_type: '12' },
  { start: 'A4349', end: '', service_type: '12' },
  { start: 'A4351', end: 'A4358', service_type: '12' },
  { start: 'A4361', end: 'A4364', service_type: '12' },
  { start: 'A4366', end: 'A4369', service_type: '12' },
  { start: 'A4371', end: 'A4373', service_type: '12' },
  { start: 'A4375', end: 'A4385', service_type: '12' },
  { start: 'A4387', end: 'A4400', service_type: '12' },
  { start: 'A4402', end: '', service_type: '12' },
  { start: 'A4404', end: 'A4435', service_type: '12' },
  { start: 'A4455', end: 'A4456', service_type: '12' },
  { start: 'A4470', end: '', service_type: '12' },
  { start: 'A4480', end: 'A4481', service_type: '12' },
  { start: 'A4483', end: '', service_type: '12' },
  { start: 'A4490', end: '', service_type: '12' },
  { start: 'A4495', end: '', service_type: '12' },
  { start: 'A4500', end: '', service_type: '12' },
  { start: 'A4510', end: '', service_type: '12' },
  { start: 'A4555', end: 'A4559', service_type: '12' },
  { start: 'A4561', end: 'A4563', service_type: '12' },
  { start: 'A4565', end: '', service_type: '12' },
  { start: 'A4570', end: '', service_type: '12' },
  { start: 'A4595', end: '', service_type: '12' },
  { start: 'A4600', end: 'A4602', service_type: '12' },
  { start: 'A4604', end: 'A4605', service_type: '12' },
  { start: 'A4608', end: '', service_type: '12' },
  { start: 'A4611', end: 'A4613', service_type: '12' },
  { start: 'A4615', end: 'A4617', service_type: '12' },
  { start: 'A4619', end: 'A4620', service_type: '12' },
  { start: 'A4623', end: 'A4626', service_type: '12' },
  { start: 'A4629', end: '', service_type: '12' },
  { start: 'A4638', end: '', service_type: '12' },
  { start: 'A5051', end: 'A5057', service_type: '12' },
  { start: 'A5061', end: 'A5063', service_type: '12' },
  { start: 'A5071', end: 'A5073', service_type: '12' },
  { start: 'A5081', end: 'A5083', service_type: '12' },
  { start: 'A5093', end: '', service_type: '12' },
  { start: 'A5102', end: '', service_type: '12' },
  { start: 'A5105', end: '', service_type: '12' },
  { start: 'A5112', end: 'A5114', service_type: '12' },
  { start: 'A5120', end: 'A5122', service_type: '12' },
  { start: 'A5126', end: '', service_type: '12' },
  { start: 'A5131', end: '', service_type: '12' },
  { start: 'A5200', end: '', service_type: '12' },
  { start: 'A6000', end: '', service_type: '12' },
  { start: 'A6413', end: '', service_type: '12' },
  { start: 'A6513', end: '', service_type: '12' },
  { start: 'A6530', end: 'A6541', service_type: '12' },
  { start: 'A6544', end: 'A6545', service_type: '12' },
  { start: 'A6549', end: 'A6550', service_type: '12' },
  { start: 'A7003', end: 'A7004', service_type: '12' },
  { start: 'A7007', end: 'A7008', service_type: '12' },
  { start: 'A7010', end: 'A7011', service_type: '12' },
  { start: 'A7013', end: '', service_type: '12' },
  { start: 'A7018', end: '', service_type: '12' },
  { start: 'A7040', end: 'A7043', service_type: '12' },
  { start: 'A7046', end: '', service_type: '12' },
  { start: 'A7048', end: '', service_type: '12' },
  { start: 'A7501', end: 'A7509', service_type: '12' },
  { start: 'A7520', end: 'A7527', service_type: '12' },
  { start: 'A9283', end: '', service_type: '12' },
  { start: 'E0100', end: 'E8002', service_type: '12' },
  { start: 'E0232', end: '', service_type: '12' },
  { start: 'E0441', end: 'E0444', service_type: '12' },
  { start: 'E0447', end: '', service_type: '12' },
  { start: 'E0485', end: '', service_type: '12' },
  { start: 'E0486', end: '', service_type: '12' },
  { start: 'E0555', end: '', service_type: '12' },
  { start: 'E0580', end: '', service_type: '12' },
  { start: 'E0785', end: '', service_type: '12' },
  { start: 'E0830', end: '', service_type: '12' },
  { start: 'E1220', end: '', service_type: '12' },
  { start: 'E1352', end: '', service_type: '12' },
  { start: 'E1701', end: 'E1702', service_type: '12' },
  { start: 'E1800', end: '', service_type: '12' },
  { start: 'E1801', end: '', service_type: '12' },
  { start: 'E1805', end: 'E1806', service_type: '12' },
  { start: 'E1810', end: 'E1812', service_type: '12' },
  { start: 'E1815', end: 'E1816', service_type: '12' },
  { start: 'E1818', end: '', service_type: '12' },
  { start: 'E1820', end: 'E1821', service_type: '12' },
  { start: 'E1825', end: '', service_type: '12' },
  { start: 'E1830', end: 'E1831', service_type: '12' },
  { start: 'E1840', end: '', service_type: '12' },
  { start: 'E2397', end: '', service_type: '12' },
  { start: 'J7340', end: '', service_type: '12' },
  { start: 'K0552', end: '', service_type: '12' },
  { start: 'K0609', end: '', service_type: '12' },
  { start: 'K0901', end: '', service_type: '12' },
  { start: 'K0902', end: '', service_type: '12' },
  { start: 'L0112', end: 'L0113', service_type: '12' },
  { start: 'L0112', end: 'L4631', service_type: '12' },
  { start: 'L0120', end: '', service_type: '12' },
  { start: 'L0130', end: '', service_type: '12' },
  { start: 'L0140', end: '', service_type: '12' },
  { start: 'L0150', end: '', service_type: '12' },
  { start: 'L0160', end: '', service_type: '12' },
  { start: 'L0170', end: '', service_type: '12' },
  { start: 'L0172', end: '', service_type: '12' },
  { start: 'L0174', end: '', service_type: '12' },
  { start: 'L0180', end: '', service_type: '12' },
  { start: 'L0190', end: '', service_type: '12' },
  { start: 'L0200', end: '', service_type: '12' },
  { start: 'L0220', end: '', service_type: '12' },
  { start: 'L0450', end: '', service_type: '12' },
  { start: 'L0452', end: '', service_type: '12' },
  { start: 'L0454', end: '', service_type: '12' },
  { start: 'L0455', end: 'L0458', service_type: '12' },
  { start: 'L0460', end: '', service_type: '12' },
  { start: 'L0462', end: '', service_type: '12' },
  { start: 'L0464', end: '', service_type: '12' },
  { start: 'L0466', end: 'L0470', service_type: '12' },
  { start: 'L0472', end: '', service_type: '12' },
  { start: 'L0480', end: '', service_type: '12' },
  { start: 'L0482', end: '', service_type: '12' },
  { start: 'L0484', end: '', service_type: '12' },
  { start: 'L0486', end: '', service_type: '12' },
  { start: 'L0488', end: '', service_type: '12' },
  { start: 'L0490', end: 'L0492', service_type: '12' },
  { start: 'L0621', end: 'L0643', service_type: '12' },
  { start: 'L0648', end: 'L0651', service_type: '12' },
  { start: 'L0700', end: '', service_type: '12' },
  { start: 'L0710', end: '', service_type: '12' },
  { start: 'L0810', end: '', service_type: '12' },
  { start: 'L0820', end: '', service_type: '12' },
  { start: 'L0830', end: '', service_type: '12' },
  { start: 'L0859', end: '', service_type: '12' },
  { start: 'L0861', end: '', service_type: '12' },
  { start: 'L0970', end: '', service_type: '12' },
  { start: 'L0972', end: '', service_type: '12' },
  { start: 'L0974', end: '', service_type: '12' },
  { start: 'L0976', end: '', service_type: '12' },
  { start: 'L0978', end: '', service_type: '12' },
  { start: 'L0980', end: '', service_type: '12' },
  { start: 'L0982', end: '', service_type: '12' },
  { start: 'L0984', end: '', service_type: '12' },
  { start: 'L0999', end: '', service_type: '12' },
  { start: 'L1000', end: '', service_type: '12' },
  { start: 'L1001', end: '', service_type: '12' },
  { start: 'L1005', end: '', service_type: '12' },
  { start: 'L1010', end: '', service_type: '12' },
  { start: 'L1020', end: '', service_type: '12' },
  { start: 'L1025', end: '', service_type: '12' },
  { start: 'L1030', end: '', service_type: '12' },
  { start: 'L1040', end: '', service_type: '12' },
  { start: 'L1050', end: '', service_type: '12' },
  { start: 'L1060', end: '', service_type: '12' },
  { start: 'L1070', end: '', service_type: '12' },
  { start: 'L1080', end: '', service_type: '12' },
  { start: 'L1085', end: '', service_type: '12' },
  { start: 'L1090', end: '', service_type: '12' },
  { start: 'L1100', end: '', service_type: '12' },
  { start: 'L1110', end: '', service_type: '12' },
  { start: 'L1120', end: '', service_type: '12' },
  { start: 'L1200', end: '', service_type: '12' },
  { start: 'L1210', end: '', service_type: '12' },
  { start: 'L1220', end: '', service_type: '12' },
  { start: 'L1230', end: '', service_type: '12' },
  { start: 'L1240', end: '', service_type: '12' },
  { start: 'L1250', end: '', service_type: '12' },
  { start: 'L1260', end: '', service_type: '12' },
  { start: 'L1270', end: '', service_type: '12' },
  { start: 'L1280', end: '', service_type: '12' },
  { start: 'L1290', end: '', service_type: '12' },
  { start: 'L1300', end: '', service_type: '12' },
  { start: 'L1310', end: '', service_type: '12' },
  { start: 'L1499', end: '', service_type: '12' },
  { start: 'L1600', end: '', service_type: '12' },
  { start: 'L1610', end: '', service_type: '12' },
  { start: 'L1620', end: '', service_type: '12' },
  { start: 'L1630', end: '', service_type: '12' },
  { start: 'L1640', end: '', service_type: '12' },
  { start: 'L1650', end: '', service_type: '12' },
  { start: 'L1652', end: '', service_type: '12' },
  { start: 'L1660', end: '', service_type: '12' },
  { start: 'L1680', end: '', service_type: '12' },
  { start: 'L1685', end: 'L1686', service_type: '12' },
  { start: 'L1690', end: '', service_type: '12' },
  { start: 'L1700', end: '', service_type: '12' },
  { start: 'L1710', end: '', service_type: '12' },
  { start: 'L1720', end: '', service_type: '12' },
  { start: 'L1730', end: '', service_type: '12' },
  { start: 'L1755', end: '', service_type: '12' },
  { start: 'L1810', end: '', service_type: '12' },
  { start: 'L1812', end: '', service_type: '12' },
  { start: 'L1820', end: '', service_type: '12' },
  { start: 'L1830', end: 'L1834', service_type: '12' },
  { start: 'L1836', end: '', service_type: '12' },
  { start: 'L1840', end: '', service_type: '12' },
  { start: 'L1843', end: 'L1848', service_type: '12' },
  { start: 'L1850', end: 'L1852', service_type: '12' },
  { start: 'L1860', end: '', service_type: '12' },
  { start: 'L1900', end: '', service_type: '12' },
  { start: 'L1902', end: '', service_type: '12' },
  { start: 'L1904', end: '', service_type: '12' },
  { start: 'L1906', end: 'L1907', service_type: '12' },
  { start: 'L1910', end: '', service_type: '12' },
  { start: 'L1920', end: '', service_type: '12' },
  { start: 'L1930', end: '', service_type: '12' },
  { start: 'L1932', end: '', service_type: '12' },
  { start: 'L1940', end: '', service_type: '12' },
  { start: 'L1945', end: '', service_type: '12' },
  { start: 'L1950', end: '', service_type: '12' },
  { start: 'L1951', end: '', service_type: '12' },
  { start: 'L1960', end: '', service_type: '12' },
  { start: 'L1970', end: '', service_type: '12' },
  { start: 'L1971', end: '', service_type: '12' },
  { start: 'L1980', end: '', service_type: '12' },
  { start: 'L1990', end: '', service_type: '12' },
  { start: 'L2000', end: '', service_type: '12' },
  { start: 'L2005', end: '', service_type: '12' },
  { start: 'L2010', end: '', service_type: '12' },
  { start: 'L2020', end: '', service_type: '12' },
  { start: 'L2030', end: '', service_type: '12' },
  { start: 'L2034', end: 'L2038', service_type: '12' },
  { start: 'L2040', end: '', service_type: '12' },
  { start: 'L2050', end: '', service_type: '12' },
  { start: 'L2060', end: '', service_type: '12' },
  { start: 'L2070', end: '', service_type: '12' },
  { start: 'L2080', end: '', service_type: '12' },
  { start: 'L2090', end: '', service_type: '12' },
  { start: 'L2106', end: '', service_type: '12' },
  { start: 'L2108', end: '', service_type: '12' },
  { start: 'L2112', end: '', service_type: '12' },
  { start: 'L2114', end: '', service_type: '12' },
  { start: 'L2116', end: '', service_type: '12' },
  { start: 'L2126', end: '', service_type: '12' },
  { start: 'L2128', end: '', service_type: '12' },
  { start: 'L2132', end: '', service_type: '12' },
  { start: 'L2134', end: '', service_type: '12' },
  { start: 'L2136', end: '', service_type: '12' },
  { start: 'L2180', end: '', service_type: '12' },
  { start: 'L2182', end: '', service_type: '12' },
  { start: 'L2184', end: '', service_type: '12' },
  { start: 'L2186', end: '', service_type: '12' },
  { start: 'L2188', end: '', service_type: '12' },
  { start: 'L2190', end: '', service_type: '12' },
  { start: 'L2192', end: '', service_type: '12' },
  { start: 'L2200', end: '', service_type: '12' },
  { start: 'L2210', end: '', service_type: '12' },
  { start: 'L2220', end: '', service_type: '12' },
  { start: 'L2230', end: '', service_type: '12' },
  { start: 'L2232', end: '', service_type: '12' },
  { start: 'L2240', end: '', service_type: '12' },
  { start: 'L2250', end: '', service_type: '12' },
  { start: 'L2260', end: '', service_type: '12' },
  { start: 'L2265', end: '', service_type: '12' },
  { start: 'L2270', end: '', service_type: '12' },
  { start: 'L2275', end: '', service_type: '12' },
  { start: 'L2280', end: '', service_type: '12' },
  { start: 'L2300', end: '', service_type: '12' },
  { start: 'L2310', end: '', service_type: '12' },
  { start: 'L2320', end: '', service_type: '12' },
  { start: 'L2330', end: '', service_type: '12' },
  { start: 'L2335', end: '', service_type: '12' },
  { start: 'L2750', end: '', service_type: '12' },
  { start: 'L2755', end: '', service_type: '12' },
  { start: 'L2760', end: '', service_type: '12' },
  { start: 'L2768', end: '', service_type: '12' },
  { start: 'L2780', end: '', service_type: '12' },
  { start: 'L2785', end: '', service_type: '12' },
  { start: 'L2795', end: '', service_type: '12' },
  { start: 'L2800', end: '', service_type: '12' },
  { start: 'L2810', end: '', service_type: '12' },
  { start: 'L2820', end: '', service_type: '12' },
  { start: 'L2830', end: '', service_type: '12' },
  { start: 'L2840', end: '', service_type: '12' },
  { start: 'L2850', end: '', service_type: '12' },
  { start: 'L2861', end: '', service_type: '12' },
  { start: 'L2999', end: 'L3003', service_type: '12' },
  { start: 'L3010', end: '', service_type: '12' },
  { start: 'L3020', end: '', service_type: '12' },
  { start: 'L3030', end: 'L3031', service_type: '12' },
  { start: 'L3040', end: '', service_type: '12' },
  { start: 'L3050', end: '', service_type: '12' },
  { start: 'L3060', end: '', service_type: '12' },
  { start: 'L3070', end: '', service_type: '12' },
  { start: 'L3080', end: '', service_type: '12' },
  { start: 'L3090', end: '', service_type: '12' },
  { start: 'L3100', end: '', service_type: '12' },
  { start: 'L3140', end: '', service_type: '12' },
  { start: 'L3150', end: '', service_type: '12' },
  { start: 'L3160', end: '', service_type: '12' },
  { start: 'L3170', end: '', service_type: '12' },
  { start: 'L3201', end: 'L3204', service_type: '12' },
  { start: 'L3206', end: 'L3209', service_type: '12' },
  { start: 'L3211', end: 'L3217', service_type: '12' },
  { start: 'L3219', end: '', service_type: '12' },
  { start: 'L3221', end: 'L3222', service_type: '12' },
  { start: 'L3224', end: 'L3225', service_type: '12' },
  { start: 'L3230', end: '', service_type: '12' },
  { start: 'L3250', end: 'L3255', service_type: '12' },
  { start: 'L3257', end: '', service_type: '12' },
  { start: 'L3260', end: '', service_type: '12' },
  { start: 'L3265', end: '', service_type: '12' },
  { start: 'L3300', end: '', service_type: '12' },
  { start: 'L3310', end: '', service_type: '12' },
  { start: 'L3320', end: '', service_type: '12' },
  { start: 'L3330', end: '', service_type: '12' },
  { start: 'L3332', end: '', service_type: '12' },
  { start: 'L3334', end: '', service_type: '12' },
  { start: 'L3340', end: '', service_type: '12' },
  { start: 'L3350', end: '', service_type: '12' },
  { start: 'L3360', end: '', service_type: '12' },
  { start: 'L3370', end: '', service_type: '12' },
  { start: 'L3380', end: '', service_type: '12' },
  { start: 'L3390', end: '', service_type: '12' },
  { start: 'L3400', end: '', service_type: '12' },
  { start: 'L3410', end: '', service_type: '12' },
  { start: 'L3420', end: '', service_type: '12' },
  { start: 'L3430', end: '', service_type: '12' },
  { start: 'L3440', end: '', service_type: '12' },
  { start: 'L3450', end: '', service_type: '12' },
  { start: 'L3455', end: '', service_type: '12' },
  { start: 'L3460', end: '', service_type: '12' },
  { start: 'L3465', end: '', service_type: '12' },
  { start: 'L3470', end: '', service_type: '12' },
  { start: 'L3480', end: '', service_type: '12' },
  { start: 'L3485', end: '', service_type: '12' },
  { start: 'L3500', end: '', service_type: '12' },
  { start: 'L3510', end: '', service_type: '12' },
  { start: 'L3520', end: '', service_type: '12' },
  { start: 'L3530', end: '', service_type: '12' },
  { start: 'L3540', end: '', service_type: '12' },
  { start: 'L3550', end: '', service_type: '12' },
  { start: 'L3560', end: '', service_type: '12' },
  { start: 'L3570', end: '', service_type: '12' },
  { start: 'L3580', end: '', service_type: '12' },
  { start: 'L3590', end: '', service_type: '12' },
  { start: 'L3595', end: '', service_type: '12' },
  { start: 'L3600', end: '', service_type: '12' },
  { start: 'L3610', end: '', service_type: '12' },
  { start: 'L3620', end: '', service_type: '12' },
  { start: 'L3630', end: '', service_type: '12' },
  { start: 'L3640', end: '', service_type: '12' },
  { start: 'L3649', end: '', service_type: '12' },
  { start: 'L3650', end: '', service_type: '12' },
  { start: 'L3660', end: '', service_type: '12' },
  { start: 'L3670', end: '', service_type: '12' },
  { start: 'L3671', end: '', service_type: '12' },
  { start: 'L3674', end: 'L3675', service_type: '12' },
  { start: 'L3677', end: '', service_type: '12' },
  { start: 'L3678', end: '', service_type: '12' },
  { start: 'L3702', end: '', service_type: '12' },
  { start: 'L3710', end: '', service_type: '12' },
  { start: 'L3720', end: '', service_type: '12' },
  { start: 'L3730', end: '', service_type: '12' },
  { start: 'L3740', end: '', service_type: '12' },
  { start: 'L3760', end: 'L3766', service_type: '12' },
  { start: 'L3806', end: 'L3809', service_type: '12' },
  { start: 'L3891', end: '', service_type: '12' },
  { start: 'L3900', end: 'L3901', service_type: '12' },
  { start: 'L3904', end: 'L3906', service_type: '12' },
  { start: 'L3908', end: '', service_type: '12' },
  { start: 'L3912', end: 'L3913', service_type: '12' },
  { start: 'L3915', end: 'L3919', service_type: '12' },
  { start: 'L3921', end: '', service_type: '12' },
  { start: 'L3923', end: 'L3925', service_type: '12' },
  { start: 'L3927', end: '', service_type: '12' },
  { start: 'L3929', end: '', service_type: '12' },
  { start: 'L3930', end: 'L3931', service_type: '12' },
  { start: 'L3933', end: '', service_type: '12' },
  { start: 'L3935', end: '', service_type: '12' },
  { start: 'L3956', end: '', service_type: '12' },
  { start: 'L3960', end: 'L3962', service_type: '12' },
  { start: 'L3967', end: '', service_type: '12' },
  { start: 'L3971', end: '', service_type: '12' },
  { start: 'L3973', end: '', service_type: '12' },
  { start: 'L3975', end: 'L3978', service_type: '12' },
  { start: 'L3980', end: 'L3982', service_type: '12' },
  { start: 'L3984', end: '', service_type: '12' },
  { start: 'L3995', end: '', service_type: '12' },
  { start: 'L3999', end: '', service_type: '12' },
  { start: 'L4000', end: '', service_type: '12' },
  { start: 'L4002', end: '', service_type: '12' },
  { start: 'L4040', end: '', service_type: '12' },
  { start: 'L4045', end: '', service_type: '12' },
  { start: 'L4050', end: '', service_type: '12' },
  { start: 'L4055', end: '', service_type: '12' },
  { start: 'L4205', end: '', service_type: '12' },
  { start: 'L4210', end: '', service_type: '12' },
  { start: 'L4350', end: '', service_type: '12' },
  { start: 'L4360', end: 'L4361', service_type: '12' },
  { start: 'L4370', end: '', service_type: '12' },
  { start: 'L4386', end: 'L4387', service_type: '12' },
  { start: 'L4392', end: '', service_type: '12' },
  { start: 'L4394', end: '', service_type: '12' },
  { start: 'L4396', end: '', service_type: '12' },
  { start: 'L4397', end: '', service_type: '12' },
  { start: 'L4398', end: '', service_type: '12' },
  { start: 'L4631', end: '', service_type: '12' },
  { start: 'L5000', end: '', service_type: '12' },
  { start: 'L7360', end: '', service_type: '12' },
  { start: 'L7362', end: '', service_type: '12' },
  { start: 'L7364', end: '', service_type: '12' },
  { start: 'L7366', end: 'L7368', service_type: '12' },
  { start: 'L7700', end: '', service_type: '12' },
  { start: 'L7902', end: '', service_type: '12' },
  { start: 'L8300', end: '', service_type: '12' },
  { start: 'L8310', end: '', service_type: '12' },
  { start: 'L8320', end: '', service_type: '12' },
  { start: 'L8330', end: '', service_type: '12' },
  { start: 'L8512', end: 'L8515', service_type: '12' },
  { start: 'L8603', end: 'L8608', service_type: '12' },
  { start: 'L8615', end: 'L8618', service_type: '12' },
  { start: 'L8622', end: 'L8625', service_type: '12' },
  { start: 'L8629', end: '', service_type: '12' },
  { start: 'L8670', end: '', service_type: '12' },
  { start: 'L8681', end: '', service_type: '12' },
  { start: 'L8683', end: 'L8684', service_type: '12' },
  { start: 'L8689', end: '', service_type: '12' },
  { start: 'L8695', end: '', service_type: '12' },
  { start: 'L8696', end: '', service_type: '12' },
  { start: 'L8698', end: '', service_type: '12' },
  { start: 'L8701', end: 'L8702', service_type: '12' },
  { start: 'Q0477', end: 'Q0509', service_type: '12' },
  { start: 'Q2049', end: '', service_type: '12' },
  { start: 'S1001', end: 'S1002', service_type: '12' },
  { start: 'S1030', end: '', service_type: '12' },
  { start: 'T5001', end: '', service_type: '12' },
  { start: 'G0175', end: '', service_type: '13' },
  { start: 'G8910', end: 'G8911', service_type: '13' },
  { start: 'G8915', end: '', service_type: '13' },
  { start: 'A4870', end: '', service_type: '14' },
  { start: 'A4215', end: '', service_type: '16' },
  { start: 'A4244', end: 'A4248', service_type: '16' },
  { start: 'A4450', end: '', service_type: '16' },
  { start: 'A4452', end: '', service_type: '16' },
  { start: 'A4651', end: 'A4653', service_type: '16' },
  { start: 'A4657', end: '', service_type: '16' },
  { start: 'A4660', end: '', service_type: '16' },
  { start: 'A4663', end: '', service_type: '16' },
  { start: 'A4670', end: 'A4674', service_type: '16' },
  { start: 'A4680', end: '', service_type: '16' },
  { start: 'A4690', end: '', service_type: '16' },
  { start: 'A4706', end: 'A4709', service_type: '16' },
  { start: 'A4714', end: '', service_type: '16' },
  { start: 'A4719', end: 'A4726', service_type: '16' },
  { start: 'A4728', end: '', service_type: '16' },
  { start: 'A4730', end: '', service_type: '16' },
  { start: 'A4736', end: 'A4737', service_type: '16' },
  { start: 'A4740', end: '', service_type: '16' },
  { start: 'A4750', end: '', service_type: '16' },
  { start: 'A4755', end: '', service_type: '16' },
  { start: 'A4760', end: '', service_type: '16' },
  { start: 'A4765', end: 'A4766', service_type: '16' },
  { start: 'A4770', end: 'A4774', service_type: '16' },
  { start: 'A4802', end: '', service_type: '16' },
  { start: 'A4860', end: '', service_type: '16' },
  { start: 'A4890', end: '', service_type: '16' },
  { start: 'A4911', end: '', service_type: '16' },
  { start: 'A4913', end: '', service_type: '16' },
  { start: 'A4918', end: '', service_type: '16' },
  { start: 'A4927', end: 'A4931', service_type: '16' },
  { start: 'A6215', end: '', service_type: '16' },
  { start: 'A6250', end: '', service_type: '16' },
  { start: 'A6260', end: '', service_type: '16' },
  { start: 'A6402', end: '', service_type: '16' },
  { start: 'E1500', end: '', service_type: '16' },
  { start: 'E1510', end: '', service_type: '16' },
  { start: 'E1520', end: '', service_type: '16' },
  { start: 'E1530', end: '', service_type: '16' },
  { start: 'E1540', end: '', service_type: '16' },
  { start: 'E1550', end: '', service_type: '16' },
  { start: 'E1560', end: '', service_type: '16' },
  { start: 'E1570', end: '', service_type: '16' },
  { start: 'E1575', end: '', service_type: '16' },
  { start: 'E1580', end: '', service_type: '16' },
  { start: 'E1590', end: '', service_type: '16' },
  { start: 'E1592', end: '', service_type: '16' },
  { start: 'E1594', end: '', service_type: '16' },
  { start: 'E1600', end: '', service_type: '16' },
  { start: 'E1610', end: '', service_type: '16' },
  { start: 'E1615', end: '', service_type: '16' },
  { start: 'E1620', end: '', service_type: '16' },
  { start: 'E1625', end: '', service_type: '16' },
  { start: 'E1630', end: '', service_type: '16' },
  { start: 'E1632', end: '', service_type: '16' },
  { start: 'E1634', end: 'E1637', service_type: '16' },
  { start: 'E1639', end: '', service_type: '16' },
  { start: 'E1699', end: '', service_type: '16' },
  { start: 'T2010', end: 'T2011', service_type: '17' },
  { start: 'A7020', end: '', service_type: '18' },
  { start: 'E0165', end: '', service_type: '18' },
  { start: 'E0181', end: 'E0182', service_type: '18' },
  { start: 'E0186', end: 'E0187', service_type: '18' },
  { start: 'E0193', end: 'E0194', service_type: '18' },
  { start: 'E0196', end: '', service_type: '18' },
  { start: 'E0202', end: '', service_type: '18' },
  { start: 'E0231', end: '', service_type: '18' },
  { start: 'E0235', end: '', service_type: '18' },
  { start: 'E0236', end: '', service_type: '18' },
  { start: 'E0250', end: 'E0251', service_type: '18' },
  { start: 'E0255', end: 'E0256', service_type: '18' },
  { start: 'E0260', end: '', service_type: '18' },
  { start: 'E0261', end: '', service_type: '18' },
  { start: 'E0265', end: 'E0266', service_type: '18' },
  { start: 'E0270', end: '', service_type: '18' },
  { start: 'E0277', end: '', service_type: '18' },
  { start: 'E0290', end: 'E0297', service_type: '18' },
  { start: 'E0301', end: 'E0305', service_type: '18' },
  { start: 'E0328', end: 'E0329', service_type: '18' },
  { start: 'E0424', end: 'E0425', service_type: '18' },
  { start: 'E0430', end: 'E0431', service_type: '18' },
  { start: 'E0433', end: '', service_type: '18' },
  { start: 'E0434', end: 'E0435', service_type: '18' },
  { start: 'E0439', end: 'E0440', service_type: '18' },
  { start: 'E0450', end: '', service_type: '18' },
  { start: 'E0455', end: '', service_type: '18' },
  { start: 'E0459', end: 'E0467', service_type: '18' },
  { start: 'E0470', end: 'E0472', service_type: '18' },
  { start: 'E0480', end: '', service_type: '18' },
  { start: 'E0482', end: 'E0483', service_type: '18' },
  { start: 'E0500', end: '', service_type: '18' },
  { start: 'E0550', end: '', service_type: '18' },
  { start: 'E0565', end: '', service_type: '18' },
  { start: 'E0570', end: '', service_type: '18' },
  { start: 'E0575', end: '', service_type: '18' },
  { start: 'E0585', end: '', service_type: '18' },
  { start: 'E0600', end: 'E0601', service_type: '18' },
  { start: 'E0606', end: '', service_type: '18' },
  { start: 'E0618', end: 'E0619', service_type: '18' },
  { start: 'E0630', end: '', service_type: '18' },
  { start: 'E0635', end: 'E0636', service_type: '18' },
  { start: 'E0675', end: '', service_type: '18' },
  { start: 'E0744', end: 'E0745', service_type: '18' },
  { start: 'E0766', end: '', service_type: '18' },
  { start: 'E0784', end: '', service_type: '18' },
  { start: 'E0791', end: '', service_type: '18' },
  { start: 'E0910', end: 'E0912', service_type: '18' },
  { start: 'E0920', end: '', service_type: '18' },
  { start: 'E0930', end: '', service_type: '18' },
  { start: 'E0935', end: 'E0936', service_type: '18' },
  { start: 'E0940', end: 'E0941', service_type: '18' },
  { start: 'E0946', end: '', service_type: '18' },
  { start: 'E0958', end: '', service_type: '18' },
  { start: 'E0968', end: '', service_type: '18' },
  { start: 'E0983', end: '', service_type: '18' },
  { start: 'E0988', end: '', service_type: '18' },
  { start: 'E1031', end: '', service_type: '18' },
  { start: 'E1035', end: 'E1039', service_type: '18' },
  { start: 'E1050', end: '', service_type: '18' },
  { start: 'E1060', end: '', service_type: '18' },
  { start: 'E1070', end: '', service_type: '18' },
  { start: 'E1083', end: 'E1090', service_type: '18' },
  { start: 'E1092', end: 'E1093', service_type: '18' },
  { start: 'E1100', end: '', service_type: '18' },
  { start: 'E1110', end: '', service_type: '18' },
  { start: 'E1130', end: '', service_type: '18' },
  { start: 'E1140', end: '', service_type: '18' },
  { start: 'E1150', end: '', service_type: '18' },
  { start: 'E1160', end: '', service_type: '18' },
  { start: 'E1170', end: 'E1172', service_type: '18' },
  { start: 'E1180', end: '', service_type: '18' },
  { start: 'E1190', end: '', service_type: '18' },
  { start: 'E1195', end: '', service_type: '18' },
  { start: 'E1200', end: '', service_type: '18' },
  { start: 'E1221', end: 'E1225', service_type: '18' },
  { start: 'E1228', end: '', service_type: '18' },
  { start: 'E1240', end: '', service_type: '18' },
  { start: 'E1250', end: '', service_type: '18' },
  { start: 'E1260', end: '', service_type: '18' },
  { start: 'E1270', end: '', service_type: '18' },
  { start: 'E1280', end: '', service_type: '18' },
  { start: 'E1285', end: '', service_type: '18' },
  { start: 'E1290', end: '', service_type: '18' },
  { start: 'E1295', end: '', service_type: '18' },
  { start: 'E1353', end: '', service_type: '18' },
  { start: 'E1355', end: '', service_type: '18' },
  { start: 'E1390', end: 'E1392', service_type: '18' },
  { start: 'E1405', end: 'E1406', service_type: '18' },
  { start: 'E1802', end: '', service_type: '18' },
  { start: 'E1841', end: '', service_type: '18' },
  { start: 'E2000', end: '', service_type: '18' },
  { start: 'E2120', end: '', service_type: '18' },
  { start: 'E2402', end: '', service_type: '18' },
  { start: 'K0001', end: 'K0004', service_type: '18' },
  { start: 'K0006', end: 'K0007', service_type: '18' },
  { start: 'K0195', end: '', service_type: '18' },
  { start: 'K0455', end: '', service_type: '18' },
  { start: 'K0730', end: '', service_type: '18' },
  { start: 'K0738', end: '', service_type: '18' },
  { start: 'K0743', end: '', service_type: '18' },
  { start: 'S1031', end: '', service_type: '18' },
  { start: 'G0009', end: '', service_type: '19' },
  { start: 'G8864', end: 'G8867', service_type: '19' },
  { start: 'G9279', end: '', service_type: '19' },
  { start: 'S0195', end: '', service_type: '19' },
  { start: 'G0155', end: '', service_type: '22' },
  { start: 'G0409', end: '', service_type: '22' },
  { start: 'H0041', end: 'H0044', service_type: '22' },
  { start: 'H1011', end: '', service_type: '22' },
  { start: 'S5140', end: 'S5141', service_type: '22' },
  { start: 'S5145', end: 'S5146', service_type: '22' },
  { start: 'S9127', end: '', service_type: '22' },
  { start: 'S9482', end: '', service_type: '22' },
  { start: 'T1018', end: '', service_type: '22' },
  { start: 'T1027', end: 'T1029', service_type: '22' },
  { start: 'G0068', end: 'G0071', service_type: '42' },
  { start: 'G0076', end: 'G0090', service_type: '42' },
  { start: 'G0156', end: '', service_type: '42' },
  { start: 'G0162', end: 'G0164', service_type: '42' },
  { start: 'G0179', end: 'G0181', service_type: '42' },
  { start: 'G0248', end: 'G0249', service_type: '42' },
  { start: 'G0490', end: '', service_type: '42' },
  { start: 'G2012', end: '', service_type: '42' },
  { start: 'G2014', end: 'G2015', service_type: '42' },
  { start: 'G2061', end: 'G2063', service_type: '42' },
  { start: 'G2250', end: 'G2252', service_type: '42' },
  { start: 'G9187', end: '', service_type: '42' },
  { start: 'S0270', end: 'S0272', service_type: '42' },
  { start: 'S0280', end: 'S0281', service_type: '42' },
  { start: 'S5035', end: 'S5036', service_type: '42' },
  { start: 'S5108', end: 'S5111', service_type: '42' },
  { start: 'S5115', end: 'S5116', service_type: '42' },
  { start: 'S5120', end: 'S5121', service_type: '42' },
  { start: 'S5130', end: 'S5131', service_type: '42' },
  { start: 'S5165', end: '', service_type: '42' },
  { start: 'S5170', end: '', service_type: '42' },
  { start: 'S5175', end: '', service_type: '42' },
  { start: 'S5180', end: 'S5181', service_type: '42' },
  { start: 'S5185', end: '', service_type: '42' },
  { start: 'S5190', end: '', service_type: '42' },
  { start: 'S5199', end: '', service_type: '42' },
  { start: 'S5497', end: '', service_type: '42' },
  { start: 'S5498', end: '', service_type: '42' },
  { start: 'S5501', end: 'S5502', service_type: '42' },
  { start: 'S5517', end: 'S5518', service_type: '42' },
  { start: 'S5520', end: 'S5523', service_type: '42' },
  { start: 'S9097', end: 'S9098', service_type: '42' },
  { start: 'S9110', end: '', service_type: '42' },
  { start: 'S9122', end: '', service_type: '42' },
  { start: 'S9325', end: 'S9328', service_type: '42' },
  { start: 'S9330', end: 'S9331', service_type: '42' },
  { start: 'S9336', end: '', service_type: '42' },
  { start: 'S9338', end: '', service_type: '42' },
  { start: 'S9340', end: 'S9343', service_type: '42' },
  { start: 'S9345', end: 'S9349', service_type: '42' },
  { start: 'S9351', end: '', service_type: '42' },
  { start: 'S9353', end: '', service_type: '42' },
  { start: 'S9355', end: '', service_type: '42' },
  { start: 'S9357', end: '', service_type: '42' },
  { start: 'S9359', end: '', service_type: '42' },
  { start: 'S9361', end: '', service_type: '42' },
  { start: 'S9363', end: 'S9368', service_type: '42' },
  { start: 'S9370', end: '', service_type: '42' },
  { start: 'S9372', end: 'S9377', service_type: '42' },
  { start: 'S9379', end: '', service_type: '42' },
  { start: 'T1022', end: '', service_type: '42' },
  { start: 'T1030', end: 'T1031', service_type: '42' },
  { start: 'S0274', end: '', service_type: '44' },
  { start: 'T1021', end: '', service_type: '44' },
  { start: 'G0182', end: '', service_type: '45' },
  { start: 'S9126', end: '', service_type: '45' },
  { start: 'T2042', end: 'T2046', service_type: '45' },
  { start: 'H0045', end: '', service_type: '46' },
  { start: 'S5150', end: 'S5151', service_type: '46' },
  { start: 'S9125', end: '', service_type: '46' },
  { start: 'T1005', end: '', service_type: '46' },
  { start: 'G2176', end: '', service_type: '47' },
  { start: 'G8818', end: 'G8838', service_type: '47' },
  { start: 'G8907', end: 'G8909', service_type: '47' },
  { start: 'G8914', end: '', service_type: '47' },
  { start: 'G9310', end: '', service_type: '47' },
  { start: 'S0201', end: '', service_type: '47' },
  { start: 'S0310', end: '', service_type: '47' },
  { start: 'G0378', end: 'G0379', service_type: '48' },
  { start: 'T2030', end: 'T2031', service_type: '54' },
  { start: 'A0080', end: '', service_type: '56' },
  { start: 'A0080', end: 'A0210', service_type: '56' },
  { start: 'A0090', end: '', service_type: '56' },
  { start: 'A0100', end: '', service_type: '56' },
  { start: 'A0110', end: '', service_type: '56' },
  { start: 'A0120', end: '', service_type: '56' },
  { start: 'A0130', end: '', service_type: '56' },
  { start: 'A0140', end: '', service_type: '56' },
  { start: 'A0160', end: '', service_type: '56' },
  { start: 'A0170', end: '', service_type: '56' },
  { start: 'A0180', end: '', service_type: '56' },
  { start: 'A0190', end: '', service_type: '56' },
  { start: 'A0200', end: '', service_type: '56' },
  { start: 'A0210', end: '', service_type: '56' },
  { start: 'A0380', end: '', service_type: '56' },
  { start: 'A0382', end: '', service_type: '56' },
  { start: 'A0384', end: '', service_type: '56' },
  { start: 'A0390', end: '', service_type: '56' },
  { start: 'A0392', end: '', service_type: '56' },
  { start: 'A0394', end: '', service_type: '56' },
  { start: 'A0396', end: '', service_type: '56' },
  { start: 'A0398', end: '', service_type: '56' },
  { start: 'A0420', end: '', service_type: '56' },
  { start: 'A0422', end: '', service_type: '56' },
  { start: 'A0424', end: 'A0426', service_type: '56' },
  { start: 'A0428', end: '', service_type: '56' },
  { start: 'A0432', end: 'A0434', service_type: '56' },
  { start: 'A0888', end: '', service_type: '56' },
  { start: 'A0998', end: 'A0999', service_type: '56' },
  { start: 'P9603', end: 'P9604', service_type: '56' },
  { start: 'R0070', end: '', service_type: '56' },
  { start: 'R0075', end: '', service_type: '56' },
  { start: 'R0076', end: '', service_type: '56' },
  { start: 'S0208', end: 'S0209', service_type: '56' },
  { start: 'S0209', end: 'S0215', service_type: '56' },
  { start: 'S0215', end: '', service_type: '56' },
  { start: 'S9381', end: '', service_type: '56' },
  { start: 'T2001', end: 'T2005', service_type: '56' },
  { start: 'T2007', end: '', service_type: '56' },
  { start: 'T2049', end: '', service_type: '56' },
  { start: 'A0430', end: 'A0431', service_type: '57' },
  { start: 'A0430', end: '', service_type: '57' },
  { start: 'A0431', end: '', service_type: '57' },
  { start: 'A0435', end: 'A0436', service_type: '57' },
  { start: 'A0435', end: 'A0436', service_type: '57' },
  { start: 'S9960', end: 'S9961', service_type: '57' },
  { start: 'S9960', end: 'S9961', service_type: '57' },
  { start: 'A0021', end: '', service_type: '59' },
  { start: 'A0380', end: 'A0426', service_type: '59' },
  { start: 'A0428', end: '', service_type: '59' },
  { start: 'A0432', end: 'A0434', service_type: '59' },
  { start: 'A0888', end: 'A0999', service_type: '59' },
  { start: 'C8903', end: 'C8913', service_type: '62' },
  { start: 'C9762', end: 'C9763', service_type: '62' },
  { start: 'G0297', end: '', service_type: '62' },
  { start: 'G9348', end: 'G9354', service_type: '62' },
  { start: 'S8042', end: '', service_type: '62' },
  { start: 'S3620', end: '', service_type: '65' },
  { start: 'S9447', end: '', service_type: '65' },
  { start: 'G0436', end: 'G0437', service_type: '67' },
  { start: 'G9016', end: '', service_type: '67' },
  { start: 'S4990', end: '', service_type: '67' },
  { start: 'S4991', end: '', service_type: '67' },
  { start: 'S4995', end: '', service_type: '67' },
  { start: 'S9453', end: '', service_type: '67' },
  { start: 'A4281', end: 'A4286', service_type: '69' },
  { start: 'A4290', end: '', service_type: '69' },
  { start: 'E0602', end: 'E0604', service_type: '69' },
  { start: 'G9357', end: 'G9358', service_type: '69' },
  { start: 'H1000', end: 'H1010', service_type: '69' },
  { start: 'K1005', end: '', service_type: '69' },
  { start: 'S0197', end: '', service_type: '69' },
  { start: 'S8415', end: '', service_type: '69' },
  { start: 'S9208', end: 'S9209', service_type: '69' },
  { start: 'S9211', end: 'S9214', service_type: '69' },
  { start: 'S9213', end: '', service_type: '69' },
  { start: 'S9436', end: 'S9439', service_type: '69' },
  { start: 'S9442', end: 'S9444', service_type: '69' },
  { start: 'T2101', end: '', service_type: '69' },
  { start: 'S9061', end: '', service_type: '72' },
  { start: 'A4641', end: 'A4642', service_type: '73' },
  { start: 'A9500', end: 'A9505', service_type: '73' },
  { start: 'A9507', end: 'A9510', service_type: '73' },
  { start: 'A9512', end: '', service_type: '73' },
  { start: 'A9515', end: 'A9516', service_type: '73' },
  { start: 'A9520', end: 'A9521', service_type: '73' },
  { start: 'A9524', end: '', service_type: '73' },
  { start: 'A9526', end: 'A9529', service_type: '73' },
  { start: 'A9531', end: '', service_type: '73' },
  { start: 'A9536', end: 'A9542', service_type: '73' },
  { start: 'A9544', end: '', service_type: '73' },
  { start: 'A9546', end: 'A9548', service_type: '73' },
  { start: 'A9550', end: 'A9562', service_type: '73' },
  { start: 'A9566', end: 'A9572', service_type: '73' },
  { start: 'A9575', end: 'A9580', service_type: '73' },
  { start: 'A9582', end: 'A9594', service_type: '73' },
  { start: 'A9597', end: 'A9599', service_type: '73' },
  { start: 'A9606', end: '', service_type: '73' },
  { start: 'A9698', end: '', service_type: '73' },
  { start: 'C2637', end: 'C2643', service_type: '73' },
  { start: 'C2698', end: 'C2699', service_type: '73' },
  { start: 'C8900', end: 'C8902', service_type: '73' },
  { start: 'C8914', end: '', service_type: '73' },
  { start: 'C8918', end: 'C8937', service_type: '73' },
  { start: 'C9068', end: '', service_type: '73' },
  { start: 'C9275', end: '', service_type: '73' },
  { start: 'C9407', end: 'C9408', service_type: '73' },
  { start: 'C9457', end: 'C9459', service_type: '73' },
  { start: 'C9461', end: '', service_type: '73' },
  { start: 'C9733', end: '', service_type: '73' },
  { start: 'C9744', end: '', service_type: '73' },
  { start: 'C9756', end: '', service_type: '73' },
  { start: 'C9776', end: '', service_type: '73' },
  { start: 'G0251', end: 'G0252', service_type: '73' },
  { start: 'G0255', end: '', service_type: '73' },
  { start: 'G0279', end: '', service_type: '73' },
  { start: 'G0288', end: '', service_type: '73' },
  { start: 'G0389', end: '', service_type: '73' },
  { start: 'G8797', end: 'G8798', service_type: '73' },
  { start: 'G8878', end: 'G8881', service_type: '73' },
  { start: 'G8882', end: 'G8885', service_type: '73' },
  { start: 'G9157', end: '', service_type: '73' },
  { start: 'Q9950', end: '', service_type: '73' },
  { start: 'Q9951', end: '', service_type: '73' },
  { start: 'Q9953', end: 'Q9968', service_type: '73' },
  { start: 'Q9982', end: 'Q9983', service_type: '73' },
  { start: 'S8037', end: '', service_type: '73' },
  { start: 'T1000', end: '', service_type: '74' },
  { start: 'C1789', end: '', service_type: '75' },
  { start: 'C1813', end: '', service_type: '75' },
  { start: 'C1815', end: '', service_type: '75' },
  { start: 'C2622', end: '', service_type: '75' },
  { start: 'C9899', end: '', service_type: '75' },
  { start: 'K0672', end: '', service_type: '75' },
  { start: 'L2340', end: '', service_type: '75' },
  { start: 'L2350', end: '', service_type: '75' },
  { start: 'L2360', end: '', service_type: '75' },
  { start: 'L2370', end: '', service_type: '75' },
  { start: 'L2375', end: '', service_type: '75' },
  { start: 'L2380', end: '', service_type: '75' },
  { start: 'L2385', end: '', service_type: '75' },
  { start: 'L2387', end: '', service_type: '75' },
  { start: 'L2390', end: '', service_type: '75' },
  { start: 'L2395', end: '', service_type: '75' },
  { start: 'L2397', end: '', service_type: '75' },
  { start: 'L2405', end: '', service_type: '75' },
  { start: 'L2415', end: '', service_type: '75' },
  { start: 'L2425', end: '', service_type: '75' },
  { start: 'L2430', end: '', service_type: '75' },
  { start: 'L2492', end: '', service_type: '75' },
  { start: 'L2500', end: '', service_type: '75' },
  { start: 'L2510', end: '', service_type: '75' },
  { start: 'L2520', end: '', service_type: '75' },
  { start: 'L2525', end: '', service_type: '75' },
  { start: 'L2526', end: '', service_type: '75' },
  { start: 'L2530', end: '', service_type: '75' },
  { start: 'L2540', end: '', service_type: '75' },
  { start: 'L2550', end: '', service_type: '75' },
  { start: 'L2570', end: '', service_type: '75' },
  { start: 'L2580', end: '', service_type: '75' },
  { start: 'L2600', end: '', service_type: '75' },
  { start: 'L2610', end: '', service_type: '75' },
  { start: 'L2620', end: '', service_type: '75' },
  { start: 'L2622', end: '', service_type: '75' },
  { start: 'L2624', end: '', service_type: '75' },
  { start: 'L2627', end: '', service_type: '75' },
  { start: 'L2628', end: '', service_type: '75' },
  { start: 'L2630', end: '', service_type: '75' },
  { start: 'L2640', end: '', service_type: '75' },
  { start: 'L2650', end: '', service_type: '75' },
  { start: 'L2660', end: '', service_type: '75' },
  { start: 'L2670', end: '', service_type: '75' },
  { start: 'L2680', end: '', service_type: '75' },
  { start: 'L4010', end: '', service_type: '75' },
  { start: 'L4020', end: '', service_type: '75' },
  { start: 'L4030', end: '', service_type: '75' },
  { start: 'L4060', end: '', service_type: '75' },
  { start: 'L4070', end: '', service_type: '75' },
  { start: 'L4080', end: '', service_type: '75' },
  { start: 'L4090', end: '', service_type: '75' },
  { start: 'L4100', end: '', service_type: '75' },
  { start: 'L4110', end: '', service_type: '75' },
  { start: 'L4130', end: '', service_type: '75' },
  { start: 'L5010', end: '', service_type: '75' },
  { start: 'L5020', end: '', service_type: '75' },
  { start: 'L5050', end: '', service_type: '75' },
  { start: 'L5060', end: '', service_type: '75' },
  { start: 'L5100', end: '', service_type: '75' },
  { start: 'L5105', end: '', service_type: '75' },
  { start: 'L5150', end: '', service_type: '75' },
  { start: 'L5160', end: '', service_type: '75' },
  { start: 'L5200', end: '', service_type: '75' },
  { start: 'L5210', end: '', service_type: '75' },
  { start: 'L5220', end: '', service_type: '75' },
  { start: 'L5230', end: '', service_type: '75' },
  { start: 'L5250', end: '', service_type: '75' },
  { start: 'L5270', end: '', service_type: '75' },
  { start: 'L5280', end: '', service_type: '75' },
  { start: 'L5301', end: '', service_type: '75' },
  { start: 'L5312', end: '', service_type: '75' },
  { start: 'L5321', end: '', service_type: '75' },
  { start: 'L5331', end: '', service_type: '75' },
  { start: 'L5341', end: '', service_type: '75' },
  { start: 'L5400', end: '', service_type: '75' },
  { start: 'L5410', end: '', service_type: '75' },
  { start: 'L5420', end: '', service_type: '75' },
  { start: 'L5430', end: '', service_type: '75' },
  { start: 'L5450', end: '', service_type: '75' },
  { start: 'L5460', end: '', service_type: '75' },
  { start: 'L5500', end: '', service_type: '75' },
  { start: 'L5505', end: '', service_type: '75' },
  { start: 'L5510', end: '', service_type: '75' },
  { start: 'L5520', end: '', service_type: '75' },
  { start: 'L5530', end: '', service_type: '75' },
  { start: 'L5535', end: '', service_type: '75' },
  { start: 'L5540', end: '', service_type: '75' },
  { start: 'L5560', end: '', service_type: '75' },
  { start: 'L5570', end: '', service_type: '75' },
  { start: 'L5580', end: '', service_type: '75' },
  { start: 'L5585', end: '', service_type: '75' },
  { start: 'L5590', end: '', service_type: '75' },
  { start: 'L5595', end: '', service_type: '75' },
  { start: 'L5600', end: '', service_type: '75' },
  { start: 'L5610', end: '', service_type: '75' },
  { start: 'L5611', end: '', service_type: '75' },
  { start: 'L5613', end: '', service_type: '75' },
  { start: 'L5614', end: '', service_type: '75' },
  { start: 'L5616', end: 'L5618', service_type: '75' },
  { start: 'L5620', end: '', service_type: '75' },
  { start: 'L5622', end: '', service_type: '75' },
  { start: 'L5624', end: '', service_type: '75' },
  { start: 'L5626', end: '', service_type: '75' },
  { start: 'L5628', end: 'L5632', service_type: '75' },
  { start: 'L5634', end: '', service_type: '75' },
  { start: 'L5636', end: 'L5640', service_type: '75' },
  { start: 'L5642', end: 'L5656', service_type: '75' },
  { start: 'L5658', end: '', service_type: '75' },
  { start: 'L5661', end: '', service_type: '75' },
  { start: 'L5665', end: 'L5666', service_type: '75' },
  { start: 'L5668', end: '', service_type: '75' },
  { start: 'L5670', end: 'L5673', service_type: '75' },
  { start: 'L5676', end: 'L5686', service_type: '75' },
  { start: 'L5688', end: '', service_type: '75' },
  { start: 'L5690', end: '', service_type: '75' },
  { start: 'L5692', end: '', service_type: '75' },
  { start: 'L5694', end: 'L5707', service_type: '75' },
  { start: 'L5710', end: 'L5712', service_type: '75' },
  { start: 'L5714', end: '', service_type: '75' },
  { start: 'L5716', end: '', service_type: '75' },
  { start: 'L5718', end: '', service_type: '75' },
  { start: 'L5722', end: '', service_type: '75' },
  { start: 'L5724', end: '', service_type: '75' },
  { start: 'L5726', end: '', service_type: '75' },
  { start: 'L5728', end: '', service_type: '75' },
  { start: 'L5780', end: 'L5782', service_type: '75' },
  { start: 'L5785', end: '', service_type: '75' },
  { start: 'L5790', end: '', service_type: '75' },
  { start: 'L5795', end: '', service_type: '75' },
  { start: 'L5810', end: 'L5812', service_type: '75' },
  { start: 'L5814', end: '', service_type: '75' },
  { start: 'L5816', end: '', service_type: '75' },
  { start: 'L5818', end: '', service_type: '75' },
  { start: 'L5822', end: '', service_type: '75' },
  { start: 'L5824', end: '', service_type: '75' },
  { start: 'L5826', end: '', service_type: '75' },
  { start: 'L5828', end: '', service_type: '75' },
  { start: 'L5830', end: '', service_type: '75' },
  { start: 'L5840', end: '', service_type: '75' },
  { start: 'L5845', end: '', service_type: '75' },
  { start: 'L5848', end: '', service_type: '75' },
  { start: 'L5850', end: '', service_type: '75' },
  { start: 'L5855', end: 'L5859', service_type: '75' },
  { start: 'L5910', end: '', service_type: '75' },
  { start: 'L5920', end: '', service_type: '75' },
  { start: 'L5925', end: '', service_type: '75' },
  { start: 'L5930', end: '', service_type: '75' },
  { start: 'L5940', end: '', service_type: '75' },
  { start: 'L5950', end: '', service_type: '75' },
  { start: 'L5960', end: '', service_type: '75' },
  { start: 'L5961', end: '', service_type: '75' },
  { start: 'L5962', end: '', service_type: '75' },
  { start: 'L5964', end: '', service_type: '75' },
  { start: 'L5966', end: 'L5976', service_type: '75' },
  { start: 'L5978', end: 'L5982', service_type: '75' },
  { start: 'L5984', end: 'L5988', service_type: '75' },
  { start: 'L5990', end: '', service_type: '75' },
  { start: 'L5999', end: 'L6000', service_type: '75' },
  { start: 'L6010', end: '', service_type: '75' },
  { start: 'L6020', end: '', service_type: '75' },
  { start: 'L6025', end: 'L6026', service_type: '75' },
  { start: 'L6050', end: '', service_type: '75' },
  { start: 'L6055', end: '', service_type: '75' },
  { start: 'L6100', end: '', service_type: '75' },
  { start: 'L6110', end: '', service_type: '75' },
  { start: 'L6120', end: '', service_type: '75' },
  { start: 'L6130', end: '', service_type: '75' },
  { start: 'L6200', end: '', service_type: '75' },
  { start: 'L6205', end: '', service_type: '75' },
  { start: 'L6250', end: '', service_type: '75' },
  { start: 'L6300', end: '', service_type: '75' },
  { start: 'L6310', end: '', service_type: '75' },
  { start: 'L6320', end: '', service_type: '75' },
  { start: 'L6350', end: '', service_type: '75' },
  { start: 'L6360', end: '', service_type: '75' },
  { start: 'L6370', end: '', service_type: '75' },
  { start: 'L6380', end: '', service_type: '75' },
  { start: 'L6382', end: '', service_type: '75' },
  { start: 'L6384', end: '', service_type: '75' },
  { start: 'L6386', end: '', service_type: '75' },
  { start: 'L6388', end: '', service_type: '75' },
  { start: 'L6400', end: '', service_type: '75' },
  { start: 'L6450', end: '', service_type: '75' },
  { start: 'L6500', end: '', service_type: '75' },
  { start: 'L6550', end: '', service_type: '75' },
  { start: 'L6570', end: '', service_type: '75' },
  { start: 'L6580', end: '', service_type: '75' },
  { start: 'L6582', end: '', service_type: '75' },
  { start: 'L6584', end: '', service_type: '75' },
  { start: 'L6586', end: '', service_type: '75' },
  { start: 'L6588', end: '', service_type: '75' },
  { start: 'L6590', end: '', service_type: '75' },
  { start: 'L6600', end: '', service_type: '75' },
  { start: 'L6605', end: '', service_type: '75' },
  { start: 'L6610', end: '', service_type: '75' },
  { start: 'L6611', end: '', service_type: '75' },
  { start: 'L6615', end: '', service_type: '75' },
  { start: 'L6616', end: '', service_type: '75' },
  { start: 'L6620', end: '', service_type: '75' },
  { start: 'L6621', end: '', service_type: '75' },
  { start: 'L6623', end: 'L6625', service_type: '75' },
  { start: 'L6628', end: 'L6630', service_type: '75' },
  { start: 'L6632', end: '', service_type: '75' },
  { start: 'L6635', end: '', service_type: '75' },
  { start: 'L6637', end: 'L6638', service_type: '75' },
  { start: 'L6640', end: 'L6642', service_type: '75' },
  { start: 'L6645', end: 'L6648', service_type: '75' },
  { start: 'L6650', end: '', service_type: '75' },
  { start: 'L6655', end: '', service_type: '75' },
  { start: 'L6660', end: '', service_type: '75' },
  { start: 'L6665', end: '', service_type: '75' },
  { start: 'L6670', end: '', service_type: '75' },
  { start: 'L6672', end: '', service_type: '75' },
  { start: 'L6675', end: 'L6677', service_type: '75' },
  { start: 'L6680', end: '', service_type: '75' },
  { start: 'L6682', end: '', service_type: '75' },
  { start: 'L6684', end: '', service_type: '75' },
  { start: 'L6686', end: 'L6698', service_type: '75' },
  { start: 'L6703', end: 'L6704', service_type: '75' },
  { start: 'L6706', end: 'L6709', service_type: '75' },
  { start: 'L6711', end: 'L6715', service_type: '75' },
  { start: 'L6721', end: 'L6722', service_type: '75' },
  { start: 'L6805', end: '', service_type: '75' },
  { start: 'L6810', end: '', service_type: '75' },
  { start: 'L6880', end: 'L6885', service_type: '75' },
  { start: 'L6890', end: '', service_type: '75' },
  { start: 'L6895', end: '', service_type: '75' },
  { start: 'L6900', end: '', service_type: '75' },
  { start: 'L6905', end: '', service_type: '75' },
  { start: 'L6910', end: '', service_type: '75' },
  { start: 'L6915', end: '', service_type: '75' },
  { start: 'L6920', end: '', service_type: '75' },
  { start: 'L6925', end: '', service_type: '75' },
  { start: 'L6930', end: '', service_type: '75' },
  { start: 'L6935', end: '', service_type: '75' },
  { start: 'L6940', end: '', service_type: '75' },
  { start: 'L6945', end: '', service_type: '75' },
  { start: 'L6950', end: '', service_type: '75' },
  { start: 'L6955', end: '', service_type: '75' },
  { start: 'L6960', end: '', service_type: '75' },
  { start: 'L6965', end: '', service_type: '75' },
  { start: 'L6970', end: '', service_type: '75' },
  { start: 'L6975', end: '', service_type: '75' },
  { start: 'L7007', end: 'L7009', service_type: '75' },
  { start: 'L7040', end: '', service_type: '75' },
  { start: 'L7045', end: '', service_type: '75' },
  { start: 'L7170', end: '', service_type: '75' },
  { start: 'L7180', end: '', service_type: '75' },
  { start: 'L7181', end: '', service_type: '75' },
  { start: 'L7185', end: 'L7186', service_type: '75' },
  { start: 'L7190', end: 'L7191', service_type: '75' },
  { start: 'L7259', end: 'L7261', service_type: '75' },
  { start: 'L7400', end: 'L7405', service_type: '75' },
  { start: 'L7499', end: '', service_type: '75' },
  { start: 'L7510', end: '', service_type: '75' },
  { start: 'L7520', end: '', service_type: '75' },
  { start: 'L7600', end: '', service_type: '75' },
  { start: 'L7900', end: '', service_type: '75' },
  { start: 'L8000', end: 'L8002', service_type: '75' },
  { start: 'L8010', end: '', service_type: '75' },
  { start: 'L8015', end: '', service_type: '75' },
  { start: 'L8020', end: '', service_type: '75' },
  { start: 'L8030', end: 'L8033', service_type: '75' },
  { start: 'L8035', end: '', service_type: '75' },
  { start: 'L8039', end: 'L8049', service_type: '75' },
  { start: 'L8400', end: '', service_type: '75' },
  { start: 'L8410', end: '', service_type: '75' },
  { start: 'L8415', end: '', service_type: '75' },
  { start: 'L8417', end: '', service_type: '75' },
  { start: 'L8420', end: '', service_type: '75' },
  { start: 'L8430', end: '', service_type: '75' },
  { start: 'L8435', end: '', service_type: '75' },
  { start: 'L8440', end: '', service_type: '75' },
  { start: 'L8460', end: '', service_type: '75' },
  { start: 'L8465', end: '', service_type: '75' },
  { start: 'L8470', end: '', service_type: '75' },
  { start: 'L8480', end: '', service_type: '75' },
  { start: 'L8485', end: '', service_type: '75' },
  { start: 'L8499', end: '', service_type: '75' },
  { start: 'L8500', end: '', service_type: '75' },
  { start: 'L8501', end: '', service_type: '75' },
  { start: 'L8505', end: '', service_type: '75' },
  { start: 'L8507', end: '', service_type: '75' },
  { start: 'L8509', end: '', service_type: '75' },
  { start: 'L8510', end: '', service_type: '75' },
  { start: 'L8511', end: '', service_type: '75' },
  { start: 'L8600', end: '', service_type: '75' },
  { start: 'L8609', end: '', service_type: '75' },
  { start: 'L8610', end: '', service_type: '75' },
  { start: 'L8612', end: 'L8614', service_type: '75' },
  { start: 'L8619', end: '', service_type: '75' },
  { start: 'L8621', end: '', service_type: '75' },
  { start: 'L8627', end: 'L8628', service_type: '75' },
  { start: 'L8630', end: 'L8631', service_type: '75' },
  { start: 'L8641', end: 'L8642', service_type: '75' },
  { start: 'L8658', end: '', service_type: '75' },
  { start: 'L8659', end: '', service_type: '75' },
  { start: 'L8679', end: 'L8680', service_type: '75' },
  { start: 'L8682', end: '', service_type: '75' },
  { start: 'L8685', end: 'L8688', service_type: '75' },
  { start: 'L8690', end: 'L8694', service_type: '75' },
  { start: 'L8699', end: '', service_type: '75' },
  { start: 'L9900', end: '', service_type: '75' },
  { start: 'S1040', end: '', service_type: '75' },
  { start: 'S8450', end: 'S8452', service_type: '75' },
  { start: 'V2623', end: 'V2629', service_type: '75' },
  { start: 'C1881', end: '', service_type: '76' },
  { start: 'G0104', end: 'G0121', service_type: '76' },
  { start: 'G0257', end: '', service_type: '76' },
  { start: 'G0365', end: '', service_type: '76' },
  { start: 'G0464', end: '', service_type: '76' },
  { start: 'G0491', end: 'G0492', service_type: '76' },
  { start: 'G8714', end: '', service_type: '76' },
  { start: 'G8956', end: 'G8957', service_type: '76' },
  { start: 'G9239', end: 'G9241', service_type: '76' },
  { start: 'G9264', end: 'G9266', service_type: '76' },
  { start: 'S3890', end: '', service_type: '76' },
  { start: 'S9335', end: '', service_type: '76' },
  { start: 'S9339', end: '', service_type: '76' },
  { start: 'G8559', end: 'G8568', service_type: '77' },
  { start: 'S0618', end: '', service_type: '77' },
  { start: 'V5008', end: '', service_type: '77' },
  { start: 'V5010', end: '', service_type: '77' },
  { start: 'V5011', end: '', service_type: '77' },
  { start: 'V5014', end: '', service_type: '77' },
  { start: 'V5020', end: '', service_type: '77' },
  { start: 'G0498', end: '', service_type: '78' },
  { start: 'J9213', end: 'J9216', service_type: '78' },
  { start: 'S9329', end: '', service_type: '78' },
  { start: 'G0010', end: '', service_type: '80' },
  { start: 'G8870', end: '', service_type: '80' },
  { start: 'M0201', end: '', service_type: '80' },
  { start: 'M0239', end: '', service_type: '80' },
  { start: 'M0243', end: 'M0248', service_type: '80' },
  { start: 'Q0239', end: '', service_type: '80' },
  { start: 'Q0243', end: '', service_type: '80' },
  { start: 'Q0245', end: '', service_type: '80' },
  { start: 'Q0247', end: '', service_type: '80' },
  { start: 'S0622', end: '', service_type: '81' },
  { start: 'A4261', end: '', service_type: '82' },
  { start: 'A4264', end: '', service_type: '82' },
  { start: 'A4266', end: 'A4269', service_type: '82' },
  { start: 'G0027', end: '', service_type: '82' },
  { start: 'J7296', end: 'J7298', service_type: '82' },
  { start: 'J7300', end: 'J7304', service_type: '82' },
  { start: 'J7306', end: 'J7307', service_type: '82' },
  { start: 'Q9984', end: '', service_type: '82' },
  { start: 'S4011', end: '', service_type: '82' },
  { start: 'S4013', end: 'S4018', service_type: '82' },
  { start: 'S4020', end: 'S4023', service_type: '82' },
  { start: 'S4025', end: 'S4028', service_type: '82' },
  { start: 'S4030', end: 'S4031', service_type: '82' },
  { start: 'S4035', end: '', service_type: '82' },
  { start: 'S4037', end: '', service_type: '82' },
  { start: 'S4040', end: '', service_type: '82' },
  { start: 'S4042', end: '', service_type: '82' },
  { start: 'S4989', end: '', service_type: '82' },
  { start: 'S4993', end: '', service_type: '82' },
  { start: 'S0199', end: '', service_type: '84' },
  { start: 'S2260', end: '', service_type: '84' },
  { start: 'S2265', end: 'S2267', service_type: '84' },
  { start: 'A0021', end: '', service_type: '86' },
  { start: 'A0225', end: '', service_type: '86' },
  { start: 'A0225', end: '', service_type: '86' },
  { start: 'A0427', end: '', service_type: '86' },
  { start: 'A0427', end: '', service_type: '86' },
  { start: 'A0429', end: '', service_type: '86' },
  { start: 'A0429', end: '', service_type: '86' },
  { start: 'G0380', end: 'G0390', service_type: '86' },
  { start: 'K0553', end: '', service_type: '86' },
  { start: 'S0207', end: '', service_type: '86' },
  { start: 'S0208', end: '', service_type: '86' },
  { start: 'S5160', end: 'S5162', service_type: '86' },
  { start: 'G0101', end: 'G0102', service_type: '87' },
  { start: 'G0103', end: '', service_type: '87' },
  { start: 'G0106', end: '', service_type: '87' },
  { start: 'G0120', end: '', service_type: '87' },
  { start: 'G0122', end: '', service_type: '87' },
  { start: 'G0219', end: '', service_type: '87' },
  { start: 'G0327', end: '', service_type: '87' },
  { start: 'G2205', end: 'G2208', service_type: '87' },
  { start: 'G3001', end: '', service_type: '87' },
  { start: 'G8464', end: 'G8465', service_type: '87' },
  { start: 'G8721', end: 'G8724', service_type: '87' },
  { start: 'G8749', end: '', service_type: '87' },
  { start: 'G8875', end: 'G8877', service_type: '87' },
  { start: 'G8927', end: 'G8929', service_type: '87' },
  { start: 'G8944', end: '', service_type: '87' },
  { start: 'G8946', end: '', service_type: '87' },
  { start: 'G8975', end: 'G8977', service_type: '87' },
  { start: 'G9050', end: 'G9139', service_type: '87' },
  { start: 'G9282', end: 'G9291', service_type: '87' },
  { start: 'S3721', end: '', service_type: '87' },
  { start: 'S8032', end: '', service_type: '87' },
  { start: 'Q0510', end: 'Q0514', service_type: '88' },
  { start: 'S9430', end: '', service_type: '88' },
  { start: 'S5001', end: '', service_type: '91' },
  { start: 'S5000', end: '', service_type: '92' },
  { start: 'G8410', end: 'G8416', service_type: '93' },
  { start: 'S0390', end: '', service_type: '93' },
  { start: 'S0395', end: '', service_type: '93' },
  { start: 'G0250', end: '', service_type: '98' },
  { start: 'G0463', end: '', service_type: 'A0' },
  { start: 'G2001', end: 'G2009', service_type: 'A3' },
  { start: 'G2013', end: '', service_type: 'A3' },
  { start: 'S0273', end: '', service_type: 'A3' },
  { start: 'G8717', end: 'G8720', service_type: 'A4' },
  { start: 'G0410', end: 'G0411', service_type: 'A6' },
  { start: 'S9480', end: '', service_type: 'A8' },
  { start: 'G8699', end: 'G8701', service_type: 'A9' },
  { start: 'S9476', end: '', service_type: 'A9' },
  { start: 'G0129', end: '', service_type: 'AD' },
  { start: 'G0152', end: '', service_type: 'AD' },
  { start: 'G0158', end: '', service_type: 'AD' },
  { start: 'G0160', end: '', service_type: 'AD' },
  { start: 'G0176', end: 'G0177', service_type: 'AD' },
  { start: 'G2169', end: '', service_type: 'AD' },
  { start: 'S9129', end: '', service_type: 'AD' },
  { start: 'G0153', end: '', service_type: 'AF' },
  { start: 'G0161', end: '', service_type: 'AF' },
  { start: 'S9128', end: '', service_type: 'AF' },
  { start: 'S9152', end: '', service_type: 'AF' },
  { start: 'G0128', end: '', service_type: 'AG' },
  { start: 'G0154', end: '', service_type: 'AG' },
  { start: 'G0299', end: 'G0300', service_type: 'AG' },
  { start: 'G0493', end: 'G0496', service_type: 'AG' },
  { start: 'S9123', end: 'S9124', service_type: 'AG' },
  { start: 'T1002', end: 'T1003', service_type: 'AG' },
  { start: 'G0396', end: 'G0397', service_type: 'AI' },
  { start: 'G0442', end: 'G0443', service_type: 'AI' },
  { start: 'G2011', end: '', service_type: 'AI' },
  { start: 'G2067', end: 'G2088', service_type: 'AI' },
  { start: 'G2172', end: '', service_type: 'AI' },
  { start: 'G2196', end: 'G2203', service_type: 'AI' },
  { start: 'G2213', end: '', service_type: 'AI' },
  { start: 'G2215', end: 'G2216', service_type: 'AI' },
  { start: 'H0001', end: 'H0016', service_type: 'AI' },
  { start: 'H0020', end: 'H0022', service_type: 'AI' },
  { start: 'H0026', end: 'H0029', service_type: 'AI' },
  { start: 'H0047', end: 'H0050', service_type: 'AI' },
  { start: 'H2034', end: 'H2036', service_type: 'AI' },
  { start: 'S9475', end: '', service_type: 'AI' },
  { start: 'T1006', end: 'T1007', service_type: 'AI' },
  { start: 'T1009', end: 'T1010', service_type: 'AI' },
  { start: 'T1012', end: '', service_type: 'AI' },
  { start: 'G0117', end: 'G0118', service_type: 'AL' },
  { start: 'G2102', end: 'G2104', service_type: 'AL' },
  { start: 'J7311', end: '', service_type: 'AL' },
  { start: 'S0500', end: '', service_type: 'AL' },
  { start: 'S0512', end: '', service_type: 'AL' },
  { start: 'S0514', end: '', service_type: 'AL' },
  { start: 'S0592', end: '', service_type: 'AL' },
  { start: 'S0595', end: '', service_type: 'AL' },
  { start: 'S0596', end: '', service_type: 'AL' },
  { start: 'S0620', end: 'S0621', service_type: 'AL' },
  { start: 'S0812', end: '', service_type: 'AL' },
  { start: 'V2500', end: 'V2503', service_type: 'AL' },
  { start: 'V2510', end: 'V2513', service_type: 'AL' },
  { start: 'V2520', end: 'V2523', service_type: 'AL' },
  { start: 'V2530', end: 'V2531', service_type: 'AL' },
  { start: 'V2599', end: 'V2600', service_type: 'AL' },
  { start: 'V2630', end: 'V2632', service_type: 'AL' },
  { start: 'V2756', end: '', service_type: 'AL' },
  { start: 'V2790', end: '', service_type: 'AL' },
  { start: 'V2797', end: '', service_type: 'AL' },
  { start: 'V2799', end: '', service_type: 'AL' },
  { start: 'S0516', end: '', service_type: 'AM' },
  { start: 'S0518', end: '', service_type: 'AM' },
  { start: 'V2020', end: '', service_type: 'AM' },
  { start: 'V2025', end: '', service_type: 'AM' },
  { start: 'S0504', end: '', service_type: 'AO' },
  { start: 'S0506', end: '', service_type: 'AO' },
  { start: 'S0508', end: '', service_type: 'AO' },
  { start: 'S0510', end: '', service_type: 'AO' },
  { start: 'S0580', end: 'S0581', service_type: 'AO' },
  { start: 'S0590', end: '', service_type: 'AO' },
  { start: 'V2100', end: 'V2115', service_type: 'AO' },
  { start: 'V2118', end: '', service_type: 'AO' },
  { start: 'V2121', end: '', service_type: 'AO' },
  { start: 'V2199', end: '', service_type: 'AO' },
  { start: 'V2200', end: 'V2221', service_type: 'AO' },
  { start: 'V2299', end: 'V2321', service_type: 'AO' },
  { start: 'V2399', end: '', service_type: 'AO' },
  { start: 'V2410', end: '', service_type: 'AO' },
  { start: 'V2430', end: '', service_type: 'AO' },
  { start: 'V2499', end: '', service_type: 'AO' },
  { start: 'V2524', end: '', service_type: 'AO' },
  { start: 'V2610', end: '', service_type: 'AO' },
  { start: 'V2615', end: '', service_type: 'AO' },
  { start: 'V2700', end: '', service_type: 'AO' },
  { start: 'V2702', end: '', service_type: 'AO' },
  { start: 'V2710', end: '', service_type: 'AO' },
  { start: 'V2715', end: '', service_type: 'AO' },
  { start: 'V2718', end: '', service_type: 'AO' },
  { start: 'V2730', end: '', service_type: 'AO' },
  { start: 'V2744', end: '', service_type: 'AO' },
  { start: 'V2745', end: '', service_type: 'AO' },
  { start: 'V2750', end: '', service_type: 'AO' },
  { start: 'V2755', end: '', service_type: 'AO' },
  { start: 'V2760', end: 'V2762', service_type: 'AO' },
  { start: 'V2770', end: '', service_type: 'AO' },
  { start: 'V2780', end: 'V2788', service_type: 'AO' },
  { start: 'A6501', end: 'A6512', service_type: 'B1' },
  { start: 'H0035', end: '', service_type: 'BB' },
  { start: 'G0424', end: '', service_type: 'BF' },
  { start: 'S9473', end: '', service_type: 'BF' },
  { start: 'G0422', end: 'G0423', service_type: 'BG' },
  { start: 'S9472', end: '', service_type: 'BG' },
  { start: 'C1849', end: '', service_type: 'BJ' },
  { start: 'C9800', end: '', service_type: 'BJ' },
  { start: 'G0429', end: '', service_type: 'BJ' },
  { start: 'G0456', end: 'G0457', service_type: 'BJ' },
  { start: 'G9292', end: 'G9295', service_type: 'BJ' },
  { start: 'C1734', end: '', service_type: 'BK' },
  { start: 'G8647', end: 'G8674', service_type: 'BK' },
  { start: 'G9233', end: 'G9234', service_type: 'BK' },
  { start: 'C1764', end: '', service_type: 'BL' },
  { start: 'C1785', end: 'C1786', service_type: 'BL' },
  { start: 'C2619', end: 'C2621', service_type: 'BL' },
  { start: 'E0616', end: 'E0617', service_type: 'BL' },
  { start: 'G2066', end: '', service_type: 'BL' },
  { start: 'G8395', end: 'G8396', service_type: 'BL' },
  { start: 'G8738', end: 'G8740', service_type: 'BL' },
  { start: 'G8923', end: '', service_type: 'BL' },
  { start: 'G8934', end: '', service_type: 'BL' },
  { start: 'G8961', end: 'G8962', service_type: 'BL' },
  { start: 'G8967', end: 'G8972', service_type: 'BL' },
  { start: 'C1783', end: 'C1784', service_type: 'BR' },
  { start: 'C1839', end: 'C1842', service_type: 'BR' },
  { start: 'G0913', end: 'G0918', service_type: 'BR' },
  { start: 'G8397', end: 'G8398', service_type: 'BR' },
  { start: 'G9376', end: 'G9377', service_type: 'BR' },
  { start: 'Q1004', end: 'Q1005', service_type: 'BR' },
  { start: 'S9150', end: '', service_type: 'BR' },
  { start: 'G0123', end: '', service_type: 'BT' },
  { start: 'G0124', end: '', service_type: 'BT' },
  { start: 'G0141', end: '', service_type: 'BT' },
  { start: 'G0143', end: '', service_type: 'BT' },
  { start: 'G0144', end: '', service_type: 'BT' },
  { start: 'G0145', end: '', service_type: 'BT' },
  { start: 'G0147', end: '', service_type: 'BT' },
  { start: 'G0148', end: '', service_type: 'BT' },
  { start: 'P3000', end: 'P3001', service_type: 'BT' },
  { start: 'Q0115', end: '', service_type: 'BT' },
  { start: 'S0610', end: '', service_type: 'BT' },
  { start: 'S0612', end: 'S0613', service_type: 'BT' },
  { start: 'G8806', end: 'G8808', service_type: 'BU' },
  { start: 'G9355', end: 'G9356', service_type: 'BU' },
  { start: 'G9361', end: '', service_type: 'BU' },
  { start: 'G0402', end: '', service_type: 'BZ' },
  { start: 'G0438', end: 'G0439', service_type: 'BZ' },
  { start: 'G0468', end: '', service_type: 'BZ' },
  { start: 'G0328', end: '', service_type: 'CL' },
  { start: 'G0202', end: '', service_type: 'CN' },
  { start: 'G0204', end: '', service_type: 'CN' },
  { start: 'G0206', end: '', service_type: 'CN' },
  { start: 'G0008', end: '', service_type: 'CO' },
  { start: 'G0919', end: '', service_type: 'CO' },
  { start: 'G8482', end: 'G8484', service_type: 'CO' },
  { start: 'Q2034', end: 'Q2039', service_type: 'CO' },
  { start: 'G0506', end: '', service_type: 'CQ' },
  { start: 'G0511', end: '', service_type: 'CQ' },
  { start: 'G2058', end: '', service_type: 'CQ' },
  { start: 'G2064', end: 'G2065', service_type: 'CQ' },
  { start: 'G9001', end: 'G9012', service_type: 'CQ' },
  { start: 'G9296', end: 'G9297', service_type: 'CQ' },
  { start: 'S0311', end: '', service_type: 'CQ' },
  { start: 'S0315', end: 'S0317', service_type: 'CQ' },
  { start: 'S0320', end: '', service_type: 'CQ' },
  { start: 'S0340', end: 'S0342', service_type: 'CQ' },
  { start: 'S0353', end: 'S0354', service_type: 'CQ' },
  { start: 'S9140', end: '', service_type: 'CQ' },
  { start: 'S9141', end: '', service_type: 'CQ' },
  { start: 'T1016', end: 'T1017', service_type: 'CQ' },
  { start: 'T2022', end: 'T2024', service_type: 'CQ' },
  { start: 'A5500', end: 'A5501', service_type: 'DM' },
  { start: 'A5503', end: 'A5508', service_type: 'DM' },
  { start: 'A5510', end: '', service_type: 'DM' },
  { start: 'A5512', end: 'A5514', service_type: 'DM' },
  { start: 'A9900', end: 'A9901', service_type: 'DM' },
  { start: 'A9999', end: '', service_type: 'DM' },
  { start: 'E0203', end: '', service_type: 'DM' },
  { start: 'E0240', end: '', service_type: 'DM' },
  { start: 'E2398', end: '', service_type: 'DM' },
  { start: 'K0739', end: 'K0740', service_type: 'DM' },
  { start: 'K0903', end: '', service_type: 'DM' },
  { start: 'K1001', end: 'K1004', service_type: 'DM' },
  { start: 'K1006', end: 'K1020', service_type: 'DM' },
  { start: 'L2006', end: '', service_type: 'DM' },
  { start: 'P9012', end: '', service_type: 'DM' },
  { start: 'T2029', end: '', service_type: 'DM' },
  { start: 'T2035', end: '', service_type: 'DM' },
  { start: 'V5030', end: '', service_type: 'DM' },
  { start: 'V5040', end: '', service_type: 'DM' },
  { start: 'V5050', end: '', service_type: 'DM' },
  { start: 'V5060', end: '', service_type: 'DM' },
  { start: 'V5070', end: '', service_type: 'DM' },
  { start: 'V5080', end: '', service_type: 'DM' },
  { start: 'V5090', end: '', service_type: 'DM' },
  { start: 'V5095', end: '', service_type: 'DM' },
  { start: 'V5100', end: '', service_type: 'DM' },
  { start: 'V5110', end: '', service_type: 'DM' },
  { start: 'V5120', end: '', service_type: 'DM' },
  { start: 'V5130', end: '', service_type: 'DM' },
  { start: 'V5140', end: '', service_type: 'DM' },
  { start: 'V5150', end: '', service_type: 'DM' },
  { start: 'V5160', end: '', service_type: 'DM' },
  { start: 'V5170', end: 'V5172', service_type: 'DM' },
  { start: 'V5180', end: 'V5181', service_type: 'DM' },
  { start: 'V5190', end: '', service_type: 'DM' },
  { start: 'V5200', end: '', service_type: 'DM' },
  { start: 'V5210', end: 'V5215', service_type: 'DM' },
  { start: 'V5220', end: 'V5221', service_type: 'DM' },
  { start: 'V5230', end: '', service_type: 'DM' },
  { start: 'V5240', end: 'V5275', service_type: 'DM' },
  { start: 'V5281', end: 'V5290', service_type: 'DM' },
  { start: 'V5298', end: 'V5299', service_type: 'DM' },
  { start: 'A4224', end: '', service_type: 'DS' },
  { start: 'A4225', end: '', service_type: 'DS' },
  { start: 'A4226', end: '', service_type: 'DS' },
  { start: 'A4230', end: '', service_type: 'DS' },
  { start: 'A4231', end: '', service_type: 'DS' },
  { start: 'A4232', end: '', service_type: 'DS' },
  { start: 'A4233', end: '', service_type: 'DS' },
  { start: 'A4234', end: '', service_type: 'DS' },
  { start: 'A4235', end: '', service_type: 'DS' },
  { start: 'A4236', end: '', service_type: 'DS' },
  { start: 'A4252', end: '', service_type: 'DS' },
  { start: 'A4253', end: '', service_type: 'DS' },
  { start: 'A4255', end: '', service_type: 'DS' },
  { start: 'A9274', end: 'A9279', service_type: 'DS' },
  { start: 'E0787', end: '', service_type: 'DS' },
  { start: 'G9147', end: '', service_type: 'DS' },
  { start: 'S1034', end: 'S1037', service_type: 'DS' },
  { start: 'S5550', end: 'S5553', service_type: 'DS' },
  { start: 'S5560', end: '', service_type: 'DS' },
  { start: 'S5561', end: '', service_type: 'DS' },
  { start: 'S5565', end: 'S5566', service_type: 'DS' },
  { start: 'S5570', end: 'S5571', service_type: 'DS' },
  { start: 'S8490', end: '', service_type: 'DS' },
  { start: 'S9145', end: '', service_type: 'DS' },
  { start: 'G0444', end: '', service_type: 'MH' },
  { start: 'G0469', end: 'G0470', service_type: 'MH' },
  { start: 'G0502', end: 'G0504', service_type: 'MH' },
  { start: 'G0507', end: '', service_type: 'MH' },
  { start: 'G0512', end: '', service_type: 'MH' },
  { start: 'G2121', end: 'G2122', service_type: 'MH' },
  { start: 'G2214', end: '', service_type: 'MH' },
  { start: 'G8126', end: 'G8128', service_type: 'MH' },
  { start: 'G8431', end: 'G8433', service_type: 'MH' },
  { start: 'G8510', end: 'G8511', service_type: 'MH' },
  { start: 'G8930', end: 'G8933', service_type: 'MH' },
  { start: 'G8947', end: 'G8948', service_type: 'MH' },
  { start: 'G8959', end: 'G8960', service_type: 'MH' },
  { start: 'G9193', end: 'G9195', service_type: 'MH' },
  { start: 'G9212', end: 'G9213', service_type: 'MH' },
  { start: 'H0017', end: 'H0019', service_type: 'MH' },
  { start: 'H0023', end: 'H0025', service_type: 'MH' },
  { start: 'H0030', end: 'H0032', service_type: 'MH' },
  { start: 'H0036', end: 'H0040', service_type: 'MH' },
  { start: 'H0046', end: '', service_type: 'MH' },
  { start: 'H2000', end: 'H2001', service_type: 'MH' },
  { start: 'H2010', end: 'H2033', service_type: 'MH' },
  { start: 'H2037', end: '', service_type: 'MH' },
  { start: 'S3005', end: '', service_type: 'MH' },
  { start: 'S9484', end: '', service_type: 'MH' },
  { start: 'S9485', end: '', service_type: 'MH' },
  { start: 'T1040', end: 'T1041', service_type: 'MH' },
  { start: 'T2034', end: '', service_type: 'MH' },
  { start: 'T2048', end: '', service_type: 'MH' },
  { start: 'G0151', end: '', service_type: 'PT' },
  { start: 'G0157', end: '', service_type: 'PT' },
  { start: 'G0159', end: '', service_type: 'PT' },
  { start: 'G2168', end: '', service_type: 'PT' },
  { start: 'S9131', end: '', service_type: 'PT' },
  { start: 'A4606', end: '', service_type: 'PU' },
  { start: 'A4614', end: '', service_type: 'PU' },
  { start: 'A4627', end: '', service_type: 'PU' },
  { start: 'G8926', end: '', service_type: 'PU' },
  { start: 'S8097', end: '', service_type: 'PU' },
  { start: 'S8100', end: 'S8101', service_type: 'PU' },
  { start: 'S8110', end: '', service_type: 'PU' },
  { start: 'S8120', end: 'S8121', service_type: 'PU' },
  { start: 'S9441', end: '', service_type: 'PU' },
  { start: 'T2032', end: 'T2033', service_type: 'RT' },
  { start: 'S9083', end: '', service_type: 'UC' },
  { start: 'S9088', end: '', service_type: 'UC' },
  { start: 'G0445', end: 'G0451', service_type: 'MH' },
  { start: 'G0452', end: '', service_type: '66' },
  { start: 'G0453', end: '', service_type: '2' },
  { start: 'G0454', end: '', service_type: '3' },
  { start: 'G0455', end: '', service_type: '5' },
  { start: 'P9612', end: '', service_type: '5' },
];

export const ST_PRIORITIZATION = [
  '2',
  '62',
  '48',
  '50',
  '13',
  '51',
  '52',
  '53',
  '47',
  '86',
  'UC',
  '7',
  '97',
  '8',
  '20',
  '21',
  'CC',
  'CD',
  '76',
  '14',
  '15',
  '16',
  '87',
  '78',
  'ON',
  '85',
  '3',
  '98',
  '4',
  '5',
  '73',
  'CK',
  'CL',
  '66',
  '17',
  '6',
  '72',
  'AR',
  'BS',
  'IC',
  'NI',
  '70',
  '63',
  '75',
  '12',
  '18',
  '11',
  'DM',
  'DS',
  '10',
  '69',
  'BI',
  'TN',
  'BH',
  '65',
  '68',
  'CM',
  'CN',
  'BT',
  'BU',
  'BV',
  '82',
  '83',
  '84',
  '42',
  '44',
  '43',
  '45',
  '46',
  'TC',
  '54',
  '74',
  'CA',
  'AG',
  'AH',
  'B1',
  'CI',
  'CJ',
  'AI',
  'AJ',
  'AK',
  '67',
  '71',
  '77',
  'BK',
  'GY',
  '79',
  'BN',
  'C1',
  'BL',
  'PU',
  'RN',
  'BP',
  'DG',
  'BJ',
  'BR',
  '93',
  '94',
  '95',
  '19',
  'CO',
  '80',
  '1',
  '22',
  'CE',
  'CF',
  'CG',
  'CH',
  'MH',
  'RT',
  'A5',
  'A6',
  'A7',
  'A8',
  'BB',
  'BC',
  'A4',
  '33',
  '34',
  'PT',
  'AD',
  'AE',
  'AF',
  'BD',
  'BG',
  'BF',
  'AB',
  'AC',
  'AA',
  'A9',
  'BE',
  '64',
  '59',
  '57',
  '58',
  '56',
  'BY',
  'BZ',
  '81',
  '99',
  'A0',
  'A3',
  'A1',
  'A2',
  '96',
  'BA',
  'AQ',
  'CQ',
  '9',
  'AM',
  'AO',
  'CP',
  'AN',
  'AL',
  '24',
  '25',
  '26',
  '27',
  '28',
  '36',
  '37',
  '38',
  '39',
  '40',
  '41',
  '91',
  'B2',
  'B3',
  'GF',
  'GN',
  '92',
  'BW',
  'BX',
  '90',
  '89',
  '88',
] as ServiceTypeCode[];
